// Enum for document types
export enum DocumentType {
  ADHAR = "ADHAR",
}
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMasterOptionName } from "../formDataHelper";
import { JOURNEY_EMAIL_REGEX } from "../../../../utils/emailValidation";
import {
  Grid,
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Skeleton,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  RootState,
  store,
  persistor,
} from "../../../../services/redux/store";
import AddIcon from "@mui/icons-material/Add";
import postsServices from "../../../../services/api/service/postsService";
import ModalPopup from "../../ModalPopup";
import { PrimaryButton } from "../../Button";
import { IconButton } from "../../Button";
import {
  removeAadhaarVerifiedDetailsCoApplicant,
  removeCoApplicantVerification,
  setCoApplicantVerification,
  setAadhaarApplicationNoCoApplicant,
  setHorizontalSteps,
  updateFormData,
  removeIsDocumentVerifiedCoApplicant,
  removeAadhaarApplicationNoCoApplicant,
  setBreStatus,
  removeFormDataKeys,
  setActiveHorizontalIndex,
  setLoanDetailCoApplicantCollapseValue,
  setVerificationMessage,
  setDisableAddMoreCoApplicant,
  setSavedBasicForCoApplicant,
  removeSavedBasicForCoApplicant,
  setSavedEmploymentForCoApplicant,
  removeSavedEmploymentForCoApplicant,
  setEncryptionKey,
} from "../../../../services/redux/slices/stepperDynamicFormSlice";
import CollapsibleSection from "../../../../pages/common-features/CollapsibleSection";
import { SectionHeader } from "../../SectionHeader";
import {
  FirstCoApplicantEmploymentNotice,
  ensureFirstCoApplicantEmploymentYes,
  isFirstCoApplicantEmploymentField,
  lockFirstCoApplicantEmploymentField,
} from "../coApplicantEmploymentLock";
import idIcon from "./../../../../assets/svgIcons/id.svg";
import locationIcon from "./../../../../assets/svgIcons/live-view.svg";
import RightBlackArrow from "./../../../../assets/svgIcons/right-black-arrow.svg";
import VerifiedIcon from "./../../../../assets/svgIcons/check-circle.svg";
import RemoveIcon from "./../../../../assets/svgIcons/remove-icon.svg";
import greenCheckVerified from "./../../../../assets/svgIcons/green-check-verified.svg";
import arrowRightVerify from "./../../../../assets/svgIcons/arrow-right-verify.svg";
import LoaderBackdrop from "../../LoaderBackdrop";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import applicantDetailsServices from "../../../../services/api/service/applicantDetailsService";
import { InputField } from "../../Inputs";
import { Field } from "formik";
import {
  mapGender,
  handleOwnershipStatusValue,
  resolveBirthCityId,
} from "../../../../utils/helpers";
import { hasCoApplicantPayPartyChanged, getFetchedCoApplicantPayParty } from "../helperDynamic";
import { buildCoApplicantSaveIdDto } from "../helperDto";
import { useApplicantMasterData } from "../../../../hooks/useApplicantMasterData";
import { fetchUserApplicationsFormData } from "../../../../services/api/service/userApplicationService";
import applicationDetailsServices from "../../../../services/api/service/applicationDetails";
import { trackCustomerJourneyEvent } from "../../../../utils/ga";
import { decrypt, encryptData } from "../../../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../../../utils/piiKey";
import dropdownService from "../../../../services/api/service/dropdownService";
import { callSaveCoApplicantApi, getCoApplicantNumberFromFieldName, getFields, getGAExtendedJourneyPrefix, getGAProvisionalOfferStatements, isCoApplicantField, targetAddressFields} from "../../../../components/ui/StepperDyanmicForm/helper";
import losServices from "../../../../services/api/service/losService";
type EmploymentTypeOption = "Salaried" | "SEP" | "SENP" | "Student" | "Others" | "Farmer";
type GenderOption = "male" | "female" | "other" | "prefer not to say";

// ─── Field name helpers (JSON-aligned) ────────────────────────────────────────
// Basic fields:      basicCoApplicant{N}<Suffix>
// Permanent addr:    addressCoApplicant{N}Permanent<Suffix>
// Current addr:      addressCoApplicant{N}Current<Suffix>
// Employment fields: employmentCoApplicant{N}<Suffix>
// Company fields:    employmentCoApplicant{N}<Suffix>

const basicField = (n: string | number, suffix: string) =>
  `basicCoApplicant${n}${suffix}`;

const permField = (n: string | number, suffix: string) =>
  `addressCoApplicant${n}permanent${suffix}`;

const currField = (n: string | number, suffix: string) =>
  `addressCoApplicant${n}current${suffix}`;

const empField = (n: string | number, suffix: string) =>
  `employmentCoApplicant${n}${suffix}`;

const companyField = (n: string | number, suffix: string) =>
  `employmentCoApplicant${n}${suffix}`;

// Regex helpers
const BASIC_REGEX = /^basicCoApplicant(\d+)/;
const PERM_REGEX = /^addressCoApplicant(\d+)permanent/;
const CURR_REGEX = /^addressCoApplicant(\d+)current(?!sameAsPermanent)/;
const EMP_REGEX = /^employmentCoApplicant(\d+)/;
const SAME_AS_PERM_REGEX = /^addressCoApplicant(\d+)currentsameAsPermanent$/;

// Monthly Obligation and Monthly Income must stay mandatory for every employment
// type (Farmer, Others, Salaried, SEP, SENP), regardless of what the field config says.
const isMandatoryEmploymentAmountField = (name: string): boolean =>
  /^employmentCoApplicant\d+monthlyObligation$/i.test(name) ||
  /^employmentCoApplicant\d+monthlyIncome$/i.test(name);

const withMandatoryEmploymentAmount = (field: any) =>
  isMandatoryEmploymentAmountField(String(field?.name || ""))
    ? { ...field, required: true }
    : field;

// Company address (address lines, landmark, pincode) becomes mandatory once the
// co-applicant's employment type is Salaried/SEP/SENP, even though the field
// config marks these fields optional by default.
const MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES = ["Salaried", "SEP", "SENP"];

const COMPANY_ADDRESS_FIELD_REGEX =
  /^employmentCoApplicant(\d+)(companyAddress1|companyAddress2|companyAddress3|landmark|pincode|city|state|country)$/i;

const withMandatoryCompanyAddress = (field: any, formVals: any) => {
  const match = String(field?.name || "").match(COMPANY_ADDRESS_FIELD_REGEX);
  if (!match) return field;

  const coAppNum = match[1];
  const employmentType = formVals?.[empField(coAppNum, "employmentType")];
  return MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES.includes(employmentType)
    ? { ...field, required: true }
    : field;
};

// Company Name is mandatory for Salaried/SEP/SENP and optional otherwise,
// regardless of what the field config says (older co-applicants may still
// have `required: true` persisted).
const COMPANY_NAME_FIELD_REGEX = /^employmentCoApplicant(\d+)companyName$/i;

const withOptionalCompanyName = (field: any, formVals: any) => {
  const match = String(field?.name || "").match(COMPANY_NAME_FIELD_REGEX);
  if (!match) return field;

  const employmentType = formVals?.[empField(match[1], "employmentType")];
  return {
    ...field,
    required: MANDATORY_COMPANY_ADDRESS_EMPLOYMENT_TYPES.includes(employmentType),
  };
};

const CO_APPLICANT_SOURCE_STEPS = [
  { horizontalIndex: 1, verticalIndex: 5 },
  { horizontalIndex: 3, verticalIndex: 1 },
];


const getAutocompleteOptionValue = (value: any): string => {
  if (value && typeof value === "object") {
    return String(value?.value ?? value?.label ?? "").trim();
  }

  return String(value ?? "").trim();
};

const getAutocompleteOptionLabel = (value: any, options: any[] = []): string => {
  const optionValue = getAutocompleteOptionValue(value);

  if (value && typeof value === "object" && value?.label) {
    return String(value.label).trim();
  }

  const matchedOption = (options || []).find(
    (option: any) =>
      String(option?.value ?? "").toLowerCase() === optionValue.toLowerCase() ||
      String(option?.label ?? "").toLowerCase() === optionValue.toLowerCase(),
  );

  return String(matchedOption?.label ?? optionValue).trim();
};

const resolveAutocompleteOption = (value: any, options: any[] = []) => {
  const optionValue = getAutocompleteOptionValue(value);

  if (!optionValue) {
    return null;
  }

  const matchedOption = (options || []).find(
    (option: any) =>
      String(option?.value ?? "").toLowerCase() === optionValue.toLowerCase() ||
      String(option?.label ?? "").toLowerCase() === optionValue.toLowerCase(),
  );

  if (matchedOption) {
    return matchedOption;
  }

  return {
    value: optionValue,
    label: getAutocompleteOptionLabel(value, options),
  };
};

const CoApplicantAdditionalDetails = ({
  fields,
  renderField,
  formikRef,
  horizontalSteps,
  activeVerticalIndex,
  activeHorizontalIndex,
  dropdownData,
  handleInvokZipCode,
  relationshipOptionsList,
  salutationOptionsList,
  zipCodeLoading,
  tabIndex,
  setTabIndex,
  checkIsLeadApiInvoked,
  handleLeadCreationFlow,
  getLeadPayload,
  values,
  errors,
  pageType = "",
}: any) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    templateId,
    applicationId,
    trackingId,
    isDocumentVerifiedCoApplicant,
    coApplicantZipCodeData,
    formData,
    coApplicantVerification,
    disableAddMoreCoApplicant,
    savedBasicCoApplicants,
    savedEmploymentCoApplicants,
    loanDetailCoApplicantCollapseValue,
    encryptionKey,
    fetchUserApplicationData,
  } = useSelector((state: RootState) => state.stepper);
  const formValues = values || formikRef?.current?.values || {};
  const [apiHorizontalSteps, setApiHorizontalSteps] = useState<any[]>([]);

  const reduxHorizontalSteps = useSelector(
    (state: RootState) => state?.stepper?.horizontalSteps,
  );
  const applicationSource = useSelector(
    (state: RootState) => state?.stepper?.applicationSource,
  );
  const leadSequenceId = useSelector(
    (state: RootState) => state?.stepper?.leadSequenceId,
  );
  const { isAuthenticated, userProfile } = useSelector(
    (state: RootState) => state.auth,
  );

  const extendedJourneyPrefix = useMemo(
      () => getGAExtendedJourneyPrefix(applicationSource),
    [applicationSource]
  );

  const {
    fetchCitiesByCountryState,
    getCityOptionsForCountry,
  } = useApplicantMasterData();

  const handleGAProvisionalOfferStatments = useMemo(
    () => getGAProvisionalOfferStatements(applicationSource),
    [applicationSource],
  );

  const currentStepFields = useMemo(
    () => (Array.isArray(fields) ? fields : []),
    [fields],
  );

  const mergedSourceHorizontalSteps = useMemo(
    () =>
      Array.isArray(reduxHorizontalSteps) && apiHorizontalSteps.length > 0
        ? reduxHorizontalSteps
        : reduxHorizontalSteps,
    [apiHorizontalSteps, reduxHorizontalSteps],
  );

  const mergedCoApplicantState = useMemo(() => {
    const grouped: Record<
      string,
      {
        basic: any[];
        address: any[];
        employment: any[];
        ids: number[];
        allFields: any[];
        sourceHorizontalIndex: number;
        sourceVerticalIndex: number;
        sourceVerticalStepId: number | null;
      }
    > = {};
    const fieldIdToSource: Record<
      string,
      {
        horizontalIndex: number;
        verticalIndex: number;
        verticalStepId: number | null;
      }
    > = {};

    CO_APPLICANT_SOURCE_STEPS.forEach(({ horizontalIndex, verticalIndex }) => {
      const sourceStep = mergedSourceHorizontalSteps?.[horizontalIndex]?.verticalSteps?.[
        verticalIndex
      ];

      if (!Array.isArray(sourceStep?.fields)) {
        return;
      }

      sourceStep.fields.forEach((field: any) => {
        const fieldName = String(field?.name || "");
        const coAppNum = getCoApplicantNumberFromFieldName(fieldName);

        if (!coAppNum || !isCoApplicantField(fieldName)) {
          return;
        }

        const coAppKey = `CoApplicant${coAppNum}`;
        if (!grouped[coAppKey]) {
          grouped[coAppKey] = {
            basic: [],
            address: [],
            employment: [],
            ids: [],
            allFields: [],
            sourceHorizontalIndex: horizontalIndex,
            sourceVerticalIndex: verticalIndex,
            sourceVerticalStepId: sourceStep?.id ?? null,
          };
        }

        grouped[coAppKey].allFields.push(field);
        if (typeof field?.id === "number") {
          grouped[coAppKey].ids.push(field.id);
          fieldIdToSource[String(field.id)] = {
            horizontalIndex,
            verticalIndex,
            verticalStepId: sourceStep?.id ?? null,
          };
        }

        if (BASIC_REGEX.test(fieldName)) {
          grouped[coAppKey].basic.push(field);
        } else if (
          PERM_REGEX.test(fieldName) ||
          CURR_REGEX.test(fieldName) ||
          SAME_AS_PERM_REGEX.test(fieldName) ||
          fieldName === `addressCoApplicant${coAppNum}SameAs`
        ) {
          grouped[coAppKey].address.push(field);
        } else if (EMP_REGEX.test(fieldName)) {
          grouped[coAppKey].employment.push(field);
        }
      });
    });

    const mergedCoApplicantFields = Object.values(grouped).flatMap(
      (group) => group.allFields,
    );
    const allPageFields = [
      ...currentStepFields,
      ...mergedCoApplicantFields.filter(
        (field: any) =>
          !currentStepFields.some(
            (currentField: any) =>
              currentField?.id === field?.id ||
              String(currentField?.name || "") === String(field?.name || ""),
          ),
      ),
    ];

    return {
      coApplicantFieldMap: grouped,
      coApplicantKeys: Object.keys(grouped).sort(
        (a, b) =>
          parseInt(a.replace("CoApplicant", ""), 10) -
          parseInt(b.replace("CoApplicant", ""), 10),
      ),
      mergedCoApplicantFields,
      allPageFields,
      fieldIdToSource,
    };
  }, [currentStepFields, mergedSourceHorizontalSteps]);
  const coApplicantFieldMap = mergedCoApplicantState.coApplicantFieldMap;
  const coApplicantKeys = mergedCoApplicantState.coApplicantKeys;
  const mergedCoApplicantFields = mergedCoApplicantState.mergedCoApplicantFields;
  const allPageFields = mergedCoApplicantState.allPageFields;
  const fieldIdToSource = mergedCoApplicantState.fieldIdToSource;

  const fetchSanitizedPiiPublicKey = useCallback(async () => {
    if (encryptionKey) {
      return getSanitizedPublicKeyFromResponse(encryptionKey);
    }
    const piiKeyResponse: any = await losServices.getPiiKey();
    if (piiKeyResponse?.status !== 200) return "";
    const rawKey = piiKeyResponse?.data?.p_k;
    const rawKeyStr = typeof rawKey === "string" ? rawKey : "";
    dispatch(setEncryptionKey(rawKeyStr));
    return getSanitizedPublicKeyFromResponse(rawKey);
  }, [encryptionKey, dispatch]);

  // ── Validate Redux schema fields against fetched application data ──────────
  const validateReduxFieldsWithFetchedData = async () => {
    try {
      const token: any = process.env.REACT_APP_FETCH_TOKEN;
      const res: any = await fetchUserApplicationsFormData(token, trackingId);
      const apps = res.data?.applications || [];
      const fetchedHorizontalSteps = Array.isArray(apps?.[0]?.horizontalSteps)
        ? apps[0].horizontalSteps
        : [];
     
      setApiHorizontalSteps(fetchedHorizontalSteps);

      const applicantLoanDetailsStep = apps?.[0]?.horizontalSteps?.find(
        (step: any) => step.horizontalStepName === "Applicant & Loan Details",
      );

      if (applicantLoanDetailsStep) {
        const loanDetailsVerticalStep =
          applicantLoanDetailsStep.verticalSteps?.find(
            (vStep: any) => vStep.verticalStepName === "Co-Applicant Details",
          );

        if (loanDetailsVerticalStep?.fields) {
          const employmentData: Record<string, any> = {};
          loanDetailsVerticalStep.fields.forEach((field: any) => {
            Object.keys(field).forEach((key) => {
              // FIX: Updated regex to match new employment field naming
              if (
                /^employmentCoApplicant\d+(isEmployed|employmentType)$/.test(
                  key,
                )
              ) {
                employmentData[key] = field[key];
              }
            });
          });

          const isEmployedKeys = Object.keys(employmentData).filter(
            (key) =>
              /^employmentCoApplicant\d+isEmployed$/.test(key) &&
              employmentData[key] &&
              employmentData[key] !== "",
          );

          let hasValidEmployment = false;
          if (isEmployedKeys.length > 0) {
            hasValidEmployment = isEmployedKeys.every((isEmployedKey) => {
              const value = employmentData[isEmployedKey];
              if (value === "Yes") {
                // FIX: Updated regex to match new field naming
                const match = isEmployedKey.match(
                  /^employmentCoApplicant(\d+)isEmployed$/,
                );
                if (match) {
                  const coApplicantNum = match[1];
                  const employmentTypeKey = `employmentCoApplicant${coApplicantNum}employmentType`;
                  return (
                    employmentData[employmentTypeKey] &&
                    employmentData[employmentTypeKey] !== ""
                  );
                }
              }
              return true;
            });
          }

          if (hasValidEmployment) {
            dispatch(setDisableAddMoreCoApplicant(false));
          }
        }
      }

      if (reduxHorizontalSteps && Array.isArray(reduxHorizontalSteps)) {
        const reduxApplicantLoanStep = reduxHorizontalSteps.find(
          (step: any) => step.stepLabel === "Applicant & Loan Details",
        );
        if (reduxApplicantLoanStep) {
          const reduxLoanDetailsStep =
            reduxApplicantLoanStep.verticalSteps?.find(
              (vStep: any) => vStep.stepLabel === "Co-Applicant Details",
            );
          if (reduxLoanDetailsStep?.fields) {
            // FIX: Updated regex to match new isEmployed field name
            const reduxIsEmployedFields = reduxLoanDetailsStep.fields
              .map((field: any) => field.name)
              .filter((name: string) =>
                /^employmentCoApplicant\d+isEmployed$/.test(name),
              );

            if (reduxIsEmployedFields.length > 0) {
              const fetchedApplicantLoanStep = apps?.[0]?.horizontalSteps?.find(
                (step: any) =>
                  step.horizontalStepName === "Applicant & Loan Details",
              );
              if (fetchedApplicantLoanStep) {
                const fetchedLoanDetailsStep =
                  fetchedApplicantLoanStep.verticalSteps?.find(
                    (vStep: any) => vStep.verticalStepName === "Co-Applicant Details",
                  );
                if (fetchedLoanDetailsStep?.fields) {
                  const fetchedIsEmployedFields = new Set<string>();
                  fetchedLoanDetailsStep.fields.forEach((field: any) => {
                    Object.keys(field).forEach((key) => {
                      // FIX: Updated regex to match new isEmployed field name
                      if (
                        /^employmentCoApplicant\d+isEmployed$/.test(
                          key,
                        ) &&
                        field[key] &&
                        field[key] !== ""
                      ) {
                        fetchedIsEmployedFields.add(key);
                      }
                    });
                  });
                  const missingFields = reduxIsEmployedFields.filter(
                    (fieldName: string) =>
                      !fetchedIsEmployedFields.has(fieldName),
                  );
                  if (missingFields.length > 0) {
                    dispatch(setDisableAddMoreCoApplicant(true));
                  }
                }
              }
            }
          }
        }
      }
    } catch (err) {
      console.error("Error validating Redux fields with fetched data:", err);
    }
  };

  useEffect(() => {
    if (applicationId && trackingId) {
      validateReduxFieldsWithFetchedData();
    }
  }, []);

  // ── Local state ────────────────────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaveNextLoading, setIsSaveNextLoading] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity?: "error" | "success";
  }>({ open: false, message: "", severity: "error" });

  const [isAadharLoading, setIsAadharLoading] = useState<
    Record<string, boolean>
  >({});

  const [panLoading, setPanLoading] = useState<Record<string, boolean>>({});
  // Inline PAN verify error per PAN field; shown in place of the schema error
  const [panVerifyError, setPanVerifyError] = useState<Record<string, string>>({});
  const [coApplicantMaritalStatusOptions, setCoApplicantMaritalStatusOptions] =
    useState<Array<{ value: string; label: string }>>([]);
  const [coApplicantCountryOptions, setCoApplicantCountryOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [coApplicantNationalityOptions, setCoApplicantNationalityOptions] =
    useState<Array<{ value: string; label: string }>>([]);
  const [addressTypeOptions, setAddressTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [propertySubTypeOptions, setPropertySubTypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("");
  
  
  // ── Co-applicant count from fields ────────────────────────────────────────
  const coAppNums = mergedCoApplicantFields
    .map((f: any) => {
      const match = f.name.match(BASIC_REGEX);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n: number | null) => n !== null);

  const numberOfCoApp = Object.keys(
    coAppNums.reduce(
      (acc: any, curr: any) => {
        acc[curr] = true;
        return acc;
      },
      {} as Record<number, boolean>,
    ),
  ).length;

  const [expanded, setExpanded] = React.useState<string | false>(
    loanDetailCoApplicantCollapseValue ||
      (typeof window !== "undefined"
        ? localStorage.getItem("coAppExpandedKey")
        : null) ||
      `CoApplicant${numberOfCoApp || 1}`,
  );

  // "Add More" auto-enable now lives further down, alongside `tabEnabled`
  // (it re-syncs disableAddMoreCoApplicant from the last co-applicant's
  // actual tab-completion state, which is itself recomputed from current
  // form values — including values restored from a fetch on mount, not
  // just after a save action).

  useEffect(() => {
    const saved =
      typeof window !== "undefined"
        ? localStorage.getItem("coAppExpandedKey")
        : null;
    if (loanDetailCoApplicantCollapseValue) {
      setExpanded(loanDetailCoApplicantCollapseValue);
    } else if (saved) {
      setExpanded(saved);
    }
  }, [loanDetailCoApplicantCollapseValue]);

  const firstCoApplicantNum = useMemo(() => {
    if (coAppNums.length === 0) return "1";
    return String(Math.min(...(coAppNums as number[])));
  }, [coAppNums]);

  const activeCoApplicantNum = useMemo(() => {
    if (typeof expanded === "string") {
      const match = expanded.match(/^CoApplicant(\d+)$/);
      if (match?.[1]) return match[1];
    }
    return firstCoApplicantNum;
  }, [expanded, firstCoApplicantNum]);

  const lastZipLookupRef = useRef<Record<string, string>>({});

  const invokeZipCodeLookup = useCallback(
    (
      zipCode: string,
      coAppNum: string,
      addressType: "permanent" | "current" | "company",
    ) => {
      const sanitizedZip = String(zipCode || "").replace(/\D/g, "");
      const lookupKey = `${coAppNum}_${addressType}`;

      if (sanitizedZip.length !== 6) {
        delete lastZipLookupRef.current[lookupKey];
        return;
      }

      if (lastZipLookupRef.current[lookupKey] === sanitizedZip) return;
      lastZipLookupRef.current[lookupKey] = sanitizedZip;
      handleInvokZipCode(sanitizedZip, coAppNum, addressType);
    },
    [handleInvokZipCode],
  );

  // Auto-trigger zipcode lookup from live Formik values so city/state/country are prefilled as user enters pincode.
  useEffect(() => {
    const coAppNum = activeCoApplicantNum;
    if (!coAppNum) return;

    const permanentZip = String(
      formValues?.[permField(coAppNum, "Pincode")] || "",
    );
    const currentZip = String(
      formValues?.[currField(coAppNum, "Pincode")] || "",
    );
    const companyZip = String(
      formValues?.[companyField(coAppNum, "pincode")] || "",
    );

    invokeZipCodeLookup(permanentZip, coAppNum, "permanent");
    invokeZipCodeLookup(currentZip, coAppNum, "current");
    invokeZipCodeLookup(companyZip, coAppNum, "company");
  }, [
    formValues?.[permField(activeCoApplicantNum, "Pincode")],
    formValues?.[currField(activeCoApplicantNum, "Pincode")],
    formValues?.[companyField(activeCoApplicantNum, "pincode")],
    activeCoApplicantNum,
    invokeZipCodeLookup,
  ]);

  // ── Options sourced from fields ───────────────────────────────────────────
  const permanentOwnershipStatusOptions =
    allPageFields?.find((f: any) => PERM_REGEX.test(f?.name) && f?.name?.endsWith("OwnershipStatus"))?.options
      ?.options ?? [];
  const currentOwnershipStatusOptions =
    allPageFields?.find((f: any) => CURR_REGEX.test(f?.name) && f?.name?.endsWith("OwnershipStatus"))?.options
      ?.options ?? [];

  // FIX 4: Updated to match new JSON field names for isEmployed and employmentType
  const isEmployedOptions =
    allPageFields?.find((f: any) => f?.name?.includes("isEmployed"))?.options
      ?.options ?? [];
  const employmentTypeOptions: EmploymentTypeOption[] =
    allPageFields?.find((f: any) =>
      /^employmentCoApplicant\d+employmentType$/.test(f?.name),
    )?.options?.options ?? [];

  const genderOptions: GenderOption[] =
    allPageFields?.find((f: any) => f?.name?.includes("Gender"))?.options?.options ??
    [];
  const maritalStatusOptions =
    coApplicantMaritalStatusOptions.length > 0
      ? coApplicantMaritalStatusOptions
      : dropdownData?.maritalStatuses ?? [];
  const countryOptions =
    coApplicantCountryOptions.length > 0
      ? coApplicantCountryOptions
      : dropdownData?.countries ?? [];
  const nationalityOptions =
    coApplicantNationalityOptions.length > 0
      ? coApplicantNationalityOptions
      : [];

  // ── Helpers ───────────────────────────────────────────────────────────────
  const updateAddMoreStateFromCoApplicantResp = (resp: any) => {
    const formData = resp?.data?.data?.formData || [];
    // FIX: Updated to match new employment field naming
    const isEmployedFields = formData.filter(
      (item: any) =>
        item.fieldKey?.includes("employmentCoApplicant") &&
        item.fieldKey?.endsWith("isEmployed"),
    );
    const hasAnyBlank = isEmployedFields.some(
      (item: any) =>
        item.fieldValue === "" ||
        item.fieldValue === null ||
        item.fieldValue === undefined,
    );
    dispatch(setDisableAddMoreCoApplicant(hasAnyBlank));
  };
  const relationshipOptions =
        allPageFields?.find((f: any) => f?.name?.includes("RelationWithStudent"))?.options
          ?.options ?? [];
  const currentVerticalStep =
    horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[
      activeVerticalIndex
    ];
  const verticalStepId = currentVerticalStep?.id;

  // ── Add co-applicant fields ───────────────────────────────────────────────
  const addCoApplicantFields = async () => {
    setIsLoading(true);
    try {
      let nextCoApplicantNum = 0;
      try {
        const countResp = await postsServices.addPost(
          "api/v1/applicant/get-coapplicant-count",
          { trackingId },
        );
        const apiCount = countResp?.data?.data?.count;
        if (typeof apiCount !== "number") {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Unable to fetch co-applicant count. Please try again.",
            }),
          );
          return;
        }
        nextCoApplicantNum = apiCount + 1;
        dispatch(setDisableAddMoreCoApplicant(true));
      } catch (e) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: "Unable to fetch co-applicant count. Please try again.",
          }),
        );
        return;
      }

      const N = nextCoApplicantNum;

      const saveAsOptions = ["Applicant"]
      for(let i=0; i< nextCoApplicantNum; i++) {
        saveAsOptions.push(`Co-Applicant ${i + 1}`);
      }

      // FIX 8: Updated employment fields in addCoApplicantFields to match JSON naming
      const apiFields = [
        // ── Basic Details ──
        {
          label: "Relationship with Student",
          name: basicField(N, "RelationWithStudent"),
          type: "autocomplete",
          required: true,
          options:relationshipOptions,
        },
        {
          label: "Specify Relationship",
          name: basicField(N, "OtherRelationWithStudent"),
          type: "text",
          required: false,
          options: null,
        },
        {
          label: "How should we address you?",
          name: basicField(N, "salutation"),
          type: "dropdown",
          required: true,
          options: { options: salutationOptionsList },
        },
        {
          label: "Co-Applicant First Name",
          name: basicField(N, "FirstName"),
          type: "text",
          required: true,
        },
        {
          label: "Co-Applicant Last Name",
          name: basicField(N, "LastName"),
          type: "text",
          required: true,
        },
        {
          label: "Gender",
          name: basicField(N, "Gender"),
          type: "dropdown",
          options: { options: genderOptions },
          required: true,
        },
        {
          label: "Date of Birth",
          name: basicField(N, "dob"),
          type: "date",
          required: true,
        },
        {
          label: "PAN",
          name: basicField(N, "PanNumber"),
          type: "text",
          required: true,
        },
        {
          label: "Co-Applicant Mobile Number",
          name: basicField(N, "PhoneNumber"),
          type: "phone",
          required: true,
        },
        {
          label: "Co-Applicant Email ID",
          name: basicField(N, "Email"),
          type: "email",
          required: true,
        },
        {
          label: "PAN Verified",
          name: basicField(N, "IsPanVerified"),
          type: "boolean",
          required: false,
        },
        {
          label: "Mobile Verified",
          name: basicField(N, "IsMobileVerified"),
          type: "boolean",
          required: false,
        },
        {
          label: "Email Verified",
          name: basicField(N, "IsEmailVerified"),
          type: "boolean",
          required: false,
        },
        
        {
          label: "Nationality",
          name: basicField(N, "Nationality"),
          type: "autocomplete",
          required: true,
          options:nationalityOptions,
        },
        {
          label: "Marital Status",
          name: basicField(N, "MaritalStatus"),
          type: "dropdown",
          required: true,
          options: { options: maritalStatusOptions },
        },
        {
          label: "Country of Birth",
          name: basicField(N, "CountryOfBirth"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "City of Birth",
          name: basicField(N, "CityOfBirth"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "Aadhaar Number",
          name: basicField(N, "AadhaarNumber"),
          type: "text",
          required: false,
        },
        {
          label: "Aadhaar Verified",
          name: basicField(N, "AadhaarVerified"),
          type: "boolean",
          required: false,
        },
        {
          label: "Do you want to consider income of the co-applicant for loan evaluation?",
          name: basicField(N, "PayParty"),
          options: {options: ["Yes", "No"]},  
          type: "radio",
          required: true,
        },

        // ── Permanent Address ──
        {
          label: "Keep the address same as",
          name: `addressCoApplicant${nextCoApplicantNum}SameAs`,
          type: "dropdown",
          required: false,
          options: { options: saveAsOptions },
        },
        {
          label: "Ownership Status",
          name: permField(N, "OwnershipStatus"),
          type: "dropdown",
          required: true,
          options: { options: permanentOwnershipStatusOptions },
        },
        {
          label: "How long have you been living here?",
          name: permField(N, "YearsInCurrentAddress"),
          type: "number",
          required: true,
        },
        {
          label: "How long have you lived in this city?",
          name: permField(N, "YearsInCurrentCity"),
          type: "number",
          required: true,
        },
        {
          label: "Address Line 1",
          name: permField(N, "AddressLine1"),
          type: "text",
          required: true,
        },
        {
          label: "Address Line 2",
          name: permField(N, "AddressLine2"),
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: permField(N, "AddressLine3"),
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: permField(N, "Pincode"),
          type: "number",
          required: true,
        },
        {
          label: "City",
          name: permField(N, "City"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "State",
          name: permField(N, "State"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "Country",
          name: permField(N, "Country"),
          type: "autocomplete",
          required: true,
        },

        // ── "Same as permanent" checkbox ──
        {
          label: "Keep current address same as permanent address",
          name: `addressCoApplicant${N}currentsameAsPermanent`,
          type: "checkbox",
          required: false,
        },

        // ── Current Address ──
        {
          label: "Ownership Status",
          name: currField(N, "OwnershipStatus"),
          type: "dropdown",
          required: true,
          options: { options: currentOwnershipStatusOptions },
        },
        {
          label: "How long have you been living here?",
          name: currField(N, "YearsInCurrentAddress"),
          type: "number",
          required: true,
        },
        {
          label: "How long have you lived in this city?",
          name: currField(N, "YearsInCurrentCity"),
          type: "number",
          required: true,
        },
        {
          label: "Address Line 1",
          name: currField(N, "AddressLine1"),
          type: "text",
          required: true,
        },
        {
          label: "Address Line 2",
          name: currField(N, "AddressLine2"),
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: currField(N, "AddressLine3"),
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: currField(N, "Pincode"),
          type: "number",
          required: true,
        },
        {
          label: "City",
          name: currField(N, "City"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "State",
          name: currField(N, "State"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "Country",
          name: currField(N, "Country"),
          type: "autocomplete",
          required: true,
        },

        // ── Employment Details — FIX 8: now using correct prefixes ──
        {
          label: "Are You Employed?",
          name: empField(N, "isEmployed"),
          type: "radio",
          required: true,
          options: { options: isEmployedOptions },
        },
        {
          label: "Employment Type",
          name: empField(N, "employmentType"),
          type: "dropdown",
          required: true,
          options: { options: employmentTypeOptions }
        },
        {
          label: "Monthly Obligation",
          name: empField(N, "monthlyObligation"),
          type: "number",
          required: true,
        },
        {
          label: "Company Name",
          name: empField(N, "companyName"),
          type: "text",
          required: false,
        },
        {
          label: "Designation",
          name: empField(N, "designation"),
          type: "autocomplete",
          required: true,
        },
        {
          label: "Monthly Income",
          name: empField(N, "monthlyIncome"),
          type: "number",
          required: true,
        },
        {
          label: "Year",
          name:  empField(N, "employmentYear"),
          required: true,
          type: "number"

        },
        {
          label: "Month", 
          name: empField(N, "employmentMonth"), 
          type: "number", 
          required: true, 
        },
        // ── Company Address — FIX 8: using companyField helper ──
        {
          label: "Address Line 1",
          name: companyField(N, "companyAddress1"),
          type: "text",
          required: false,
        },
        {
          label: "Address Line 2",
          name: companyField(N, "companyAddress2"),
          type: "text",
          required: false,
        },
        {
          label: "Address Line 3",
          name: companyField(N, "companyAddress3"),
          type: "text",
          required: false,
        },
        {
          label: "Landmark",
          name: companyField(N, "landmark"),
          type: "text",
          required: false,
        },
        {
          label: "Pincode",
          name: companyField(N, "pincode"),
          type: "number",
          required: false,
        },
        {
          label: "City",
          name: companyField(N, "city"),
          type: "text",
          required: false,
        },
        {
          label: "State",
          name: companyField(N, "state"),
          type: "text",
          required: false,
        },
        {
          label: "Country",
          name: companyField(N, "companyCountry"),
          type: "autocomplete",
          required: false,
        },
      ];

      const resp = await postsServices.addPost(
        "api/v1/product/store-user-fields",
        {
          vertical_step_id: verticalStepId,
          trackingId: trackingId,
          fields: apiFields,
        },
      );

      const storedFields = Array.isArray(resp?.data?.data)
        ? resp?.data?.data
        : Array.isArray(resp?.data?.data?.fields)
          ? resp?.data?.data?.fields
          : [];

      if (resp?.data?.status === "success" && storedFields.length > 0) {
        const newFields = storedFields.map((f: any) => ({
          ...f,
          options: f?.options ? f?.options : null,
        }));

        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const targetHorizontalIdx =
            typeof activeHorizontalIndex === "number" &&
            activeHorizontalIndex >= 0 &&
            activeHorizontalIndex < horizontalSteps.length
              ? activeHorizontalIndex
              : -1;
          if (targetHorizontalIdx !== -1) {
            const targetHorizontalStep = {
              ...horizontalSteps[targetHorizontalIdx],
            };
            const verticalSteps = [...targetHorizontalStep.verticalSteps];
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = [...targetStep.fields, ...newFields];
              verticalSteps[targetVerticalIdx] = targetStep;
              targetHorizontalStep.verticalSteps = verticalSteps;
              horizontalSteps[targetHorizontalIdx] = targetHorizontalStep;
              dispatch(setHorizontalSteps(horizontalSteps));
              setApiHorizontalSteps(horizontalSteps);
            }
          }
        }

        if (formikRef?.current) {
          newFields.forEach((field: any) => {
            let defaultValue: any = "";
            if (
              field.type === "dropdown" &&
              field.options?.options?.length > 0
            ) {
              defaultValue = "";
            } else if (field.type === "boolean") {
              defaultValue = false;
            }
            formikRef.current.setFieldValue(field.name, defaultValue, false);
          });
        }

        const nextAccordionKey = `CoApplicant${N}`;
        setExpanded(nextAccordionKey);
        dispatch(setLoanDetailCoApplicantCollapseValue(nextAccordionKey));
        setTabIndex((prev: any) => ({ ...prev, [nextAccordionKey]: 0 }));
        if (typeof window !== "undefined") {
          localStorage.setItem("coAppExpandedKey", nextAccordionKey);
        }
      }
    } catch (err) {
      console.error("Add entry failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Check whether a co-applicant genuinely has saved data ──────────────────
  // Looks across the two places co-applicant basic/address/employment fields
  // get persisted into the application data:
  //   1. "Applicant & Loan Details" horizontal step → "Co-Applicant Details" vertical
  //   2. "Additional Details & Documents" horizontal step → "Co-Applicant
  //      Additional Details" vertical (this component's own step; the
  //      backend has shipped this name both with and without the typo, so
  //      both spellings are matched)
  // Returns true as soon as either location has a non-empty
  // basic/address/employmentCoApplicant<N> field, i.e. this co-applicant was
  // actually saved server-side — regardless of what savedBasicCoApplicants
  // (which only reflects same-session saves) currently holds.
  //
  // Hits the fetch-application-data API directly (same call
  // validateReduxFieldsWithFetchedData uses) rather than reading the
  // Redux-cached fetchUserApplicationData, so this always checks against the
  // latest server state instead of a snapshot that may be stale (e.g. a
  // co-applicant saved just moments ago elsewhere in the same session).
  const isCoApplicantSavedInApplicationData = async (
    coApplicantNumber: number | string,
  ): Promise<boolean> => {
    let steps: any[] | undefined;
    try {
      const token: any = process.env.REACT_APP_FETCH_TOKEN;
      const res: any = await fetchUserApplicationsFormData(token, trackingId);
      steps = res?.data?.applications?.[0]?.horizontalSteps;
    } catch (err) {
      console.error(
        "Error fetching application data for isCoApplicantSavedInApplicationData",
        err,
      );
    }
    if (!Array.isArray(steps)) {
      // Fall back to the last known Redux snapshot if the fresh fetch failed.
      steps = fetchUserApplicationData?.horizontalSteps;
    }
    if (!Array.isArray(steps)) return false;

    const applicantLoanStep = steps.find(
      (step: any) => step?.horizontalStepName === "Applicant & Loan Details",
    );
    const documentsStep = steps.find(
      (step: any) =>
        step?.horizontalStepName === "Additional Details & Documents",
    );

    const loanDetailsVStep = applicantLoanStep?.verticalSteps?.find(
      (vStep: any) => vStep?.verticalStepName === "Co-Applicant Details",
    );
    const coApplicantAdditionalDetailsVStep = documentsStep?.verticalSteps?.find(
      (vStep: any) =>
        vStep?.verticalStepName === "Co-Applicant Additional Details" ||
        vStep?.verticalStepName === "Co-Applicant Aditional Details",
    );

    const fieldNameRegex = new RegExp(
      `^(basic|address|employment)CoApplicant${coApplicantNumber}(?!\\d)`,
    );

    const vStepHasSavedCoApplicantField = (vStep: any): boolean => {
      if (!Array.isArray(vStep?.fields)) return false;
      return vStep.fields.some((fieldObj: any) => {
        if (!fieldObj || typeof fieldObj !== "object") return false;
        const key = Object.keys(fieldObj)[0];
        if (!key || !fieldNameRegex.test(key)) return false;
        const rawValue = fieldObj[key];
        const resolved =
          rawValue && typeof rawValue === "object"
            ? (rawValue as any).value ?? (rawValue as any).label
            : rawValue;
        return String(resolved ?? "").trim() !== "";
      });
    };

    return (
      vStepHasSavedCoApplicantField(loanDetailsVStep) ||
      vStepHasSavedCoApplicantField(coApplicantAdditionalDetailsVStep)
    );
  };

  // ── Remove co-applicant fields ────────────────────────────────────────────
  const removeCoApplicantFields = async (fieldIds: number[]) => {

    let coAppIndex: string | null = null;
    let coAppNum: string | null = null;
    const allFields = allPageFields;

    for (const field of allFields) {
      if (fieldIds.includes(field.id)) {
        // FIX 10: Updated regex to match all three naming patterns
        const match =
          field.name.match(/^basicCoApplicant(\d+)/) ||
          field.name.match(/^addressCoApplicant(\d+)/) ||
          field.name.match(/^employmentCoApplicant(\d+)/);
        if (match) {
          coAppIndex = String(Number(match[1]) - 1);
          coAppNum = match[1];
          break;
        }
      }
    }
    
    if (coAppNum) {
      const coAppKey = `CoApplicant${coAppNum}`;
      
      if ((await isCoApplicantSavedInApplicationData(coAppNum)) === true) {
        let isCoApplicantDeleted = true;
        try {
          const losPayload = {
            trackingId: trackingId,
            coApplicantType: `Co-Applicant ${coAppNum}`,
          };
          let applicantIdForDelete = "0";
          try {
            const resp: any =
              await losServices.getCoApplicantDetails(losPayload);
            const apiApplicantId = resp?.data?.data?.applicantId;
            applicantIdForDelete =
              apiApplicantId === null || apiApplicantId === undefined
                ? "0"
                : apiApplicantId || "0";
          } catch (e) {
            applicantIdForDelete = "0";
          }
          const deleteResp: any = await applicationDetailsServices.deleteCoApplicant(
            trackingId,
            `Co-Applicant ${coAppNum}`,
          );
          const deleteStatus = deleteResp?.data?.status;
          if (deleteStatus && String(deleteStatus).toLowerCase() !== "success") {
            isCoApplicantDeleted = false;
          }
          if (isCoApplicantDeleted && savedBasicCoApplicants?.[coAppKey]) {
            dispatch(removeSavedBasicForCoApplicant(coAppKey));
          }
          if (isCoApplicantDeleted && savedEmploymentCoApplicants?.[coAppKey]) {
            dispatch(removeSavedEmploymentForCoApplicant(coAppKey));
          }
        } catch (err) {
          isCoApplicantDeleted = false;
          console.error("Error calling delete-coapplicant API", err);
        }
        // Saved co-applicant: delete-user-fields only runs once
        // delete-coapplicant succeeds.
        if (!isCoApplicantDeleted) {
          dispatch(
            setStatusPopup({
              status: "error",
              message: "Co-applicant cannot be deleted as they are added as a co-owner in collateral.",
            }),
          );
          return;
        }
      }
    }

    // Unsaved co-applicant: delete-user-fields is called straight away;
    // saved co-applicant: right after delete-coapplicant succeeded above.

    try {
      const resp = await postsServices.addPost(
        "api/v1/product/delete-user-fields",
        {
          form_field_ids: fieldIds,
          trackingId: trackingId,
        },
      );

      if (resp.data?.status === "success") {
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const sourceStepForDelete = fieldIds
            .map((fieldId) => fieldIdToSource[String(fieldId)])
            .find(Boolean);
          const targetHorizontalIdx =
            typeof sourceStepForDelete?.horizontalIndex === "number" &&
            sourceStepForDelete.horizontalIndex >= 0 &&
            sourceStepForDelete.horizontalIndex < horizontalSteps.length
              ? sourceStepForDelete.horizontalIndex
              : -1;
          if (targetHorizontalIdx !== -1) {
            const targetHorizontalStep = {
              ...horizontalSteps[targetHorizontalIdx],
            };
            const verticalSteps = [...targetHorizontalStep.verticalSteps];
            const targetVerticalIdx = sourceStepForDelete?.verticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = targetStep.fields.filter(
                (field: any) => !fieldIds.includes(field.id),
              );
              verticalSteps[targetVerticalIdx] = targetStep;
              targetHorizontalStep.verticalSteps = verticalSteps;
              horizontalSteps[targetHorizontalIdx] = targetHorizontalStep;
              dispatch(setHorizontalSteps(horizontalSteps));
              setApiHorizontalSteps(horizontalSteps);
            }
          }
        }

        const fieldNamesToClear: string[] = [];
        allPageFields.forEach((field: any) => {
          if (fieldIds.includes(field.id)) {
            fieldNamesToClear.push(field.name);
          }
        });

        if (formikRef?.current && coAppIndex !== null) {
          const prefix = `basicCoApplicant${Number(coAppIndex) + 1}`;
          const newValues = { ...formikRef.current.values };
          Object.keys(newValues).forEach((key) => {
            if (key.startsWith(prefix)) {
              delete newValues[key];
            }
          });
          formikRef.current.setValues(newValues, false);
          formikRef.current.validateForm();
        } else if (formikRef?.current) {
          fieldNamesToClear.forEach((name) => {
            const isBoolean =
              name?.toLowerCase().includes("is") ||
              name?.toLowerCase().includes("verified");
            formikRef.current.setFieldValue(
              name,
              isBoolean ? false : "",
              false,
            );
          });
        }
        dispatch(setDisableAddMoreCoApplicant(hasRemainingUnsavedBlankCoApplicant(coAppNum)));
      }
    } catch (err) {
      console.error("Error deleting coapplicant fields", err);
    }

    if (coAppIndex !== null) {
      dispatch(removeCoApplicantVerification(coAppIndex));
    }

    if (coAppNum) {
      const coAppKey = `CoApplicant${coAppNum}`;
      dispatch(removeAadhaarVerifiedDetailsCoApplicant(coAppKey));
      dispatch(removeIsDocumentVerifiedCoApplicant(coAppKey));
      dispatch(removeAadhaarApplicationNoCoApplicant({ coAppNum, coAppKey }));

      if (formikRef?.current) {
        const values = { ...formikRef?.current?.values };
        const keysToRemove: string[] = [];
        const basicRegex = new RegExp(`^basicCoApplicant${coAppNum}`);
        const empRegex = new RegExp(`^employmentCoApplicant${coAppNum}`);
        const addrRegex = new RegExp(`^addressCoApplicant${coAppNum}`);
        Object.keys(values).forEach((key) => {
          if (
            basicRegex.test(key) ||
            empRegex.test(key) ||
            addrRegex.test(key)
          ) {
            keysToRemove.push(key);
            delete values[key];
          }
        });
        if (keysToRemove.length > 0) {
          dispatch(removeFormDataKeys(keysToRemove));
        }
        formikRef?.current.setValues(values, false);
        formikRef?.current.validateForm();
      }
    }

  };

  const handleRemoveClick = (ids: number[]) => {
    setPendingRemoveIds(ids);
    setConfirmOpen(true);
  };
  const handleConfirmRemove = () => {
    console.log(pendingRemoveIds,"pendingRemoveIds")
    if (pendingRemoveIds !== null) {
      removeCoApplicantFields(pendingRemoveIds);
    }
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };
  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  // ── Proceed (provisional offer) ───────────────────────────────────────────
  const handleProceedClick = async () => {
    dispatch(setBreStatus("FROM_FORM"));
    const data: any[] = [];
    const values = formikRef?.current?.values || {};
    const collateralValue = values["collateral"];
    fields.forEach((field: any) => {
      if (
        field.name === "LoanDetailsLoanAmount" ||
        field.name === "collateral" ||
        (collateralValue === "Yes" &&
          (field.name === "collateralType" ||
            field.name === "collateralAmount"))
      ) {
        data.push({
          formFieldId: field.id,
          formFieldKey: field.name,
          formFieldValue: values[field.name] ?? "",
        });
      }
    });
    const payloadObject = {
      data,
      trackingId,
      templateId,
      formName: horizontalSteps?.[activeHorizontalIndex]?.stepLabel,
      mobileNumber: values?.phone_number,
    };

    try {
      dispatch(updateFormData(values));
      await applicantDetailsServices
        .createApplicant(payloadObject)
        .then(() => {
          trackCustomerJourneyEvent(
            `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
            isAuthenticated ? (decrypt(userProfile?.id) ?? "") : "",
            trackingId,
            {
              label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
              category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
              action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_PROVISIONALOFFER_PAGE10`,
              page: "ProvisionalOff_Loan Details_Provisional Offer",
            },
          );
          dispatch(setActiveHorizontalIndex(activeHorizontalIndex + 1));
        })
        .catch((err) => {
          const apiErrorMessage = err.response?.data?.message || "";
          let displayMessage = "Something went wrong while saving data.";
          if (apiErrorMessage.includes("LoanDetailsLoanAmount")) {
            displayMessage =
              "Please enter a loan amount that matches our lending policy to proceed.";
          } else if (apiErrorMessage) {
            displayMessage = apiErrorMessage;
          }
          setSnackbar({
            open: true,
            message: displayMessage,
            severity: "error",
          });
        });
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Something went wrong while saving data.",
        severity: "error",
      });
    }
  };
  
  const hasRemainingUnsavedBlankCoApplicant = (removedCoAppNum?: string | null) => {
    const allFields = Array.isArray(fields)
      ? fields[0]?.fields
        ? fields.flatMap((g: any) => g.fields)
        : fields
      : [];
    const values = formikRef?.current?.values || {};
    const coApplicantNums = new Set<string>();

    allFields.forEach((field: any) => {
      const match = field?.name?.match(/^(basic|address|employment)CoApplicant(\d+)/);
      if (match && match[2] !== removedCoAppNum) {
        coApplicantNums.add(match[2]);
      }
    });

    return Array.from(coApplicantNums).some((num) => {
      if (savedBasicCoApplicants?.[`CoApplicant${num}`]) return false;
      return allFields.some((field: any) => {
        if (
          !field?.required ||
          !field?.name?.startsWith(`basicCoApplicant${num}`)
        ) {
          return false;
        }
        const value = values[field.name];
        return value === undefined || value === null || value === "";
      });
    });
  };
  console.log(savedBasicCoApplicants,"savedBasicCoApplicants")
  // ── Formik sync effects ───────────────────────────────────────────────────
  useEffect(() => {
    if (!formikRef?.current || !formikRef?.current?.values) return;
    const currentFieldNames = allPageFields?.map((field: any) => field?.name);
    const allValues = { ...formikRef?.current?.values };

    // FIX 9: Also clean up employment keys with new prefix
    const coApplicantKeys = Object.keys(allValues).filter(
      (key) => BASIC_REGEX.test(key) || EMP_REGEX.test(key),
    );
    const keysToRemove = coApplicantKeys.filter(
      (key) => !currentFieldNames.includes(key),
    );
    keysToRemove.forEach((key) => {
      delete allValues[key];
    });

    // Set defaults for new fields
    currentFieldNames.forEach((name: any) => {
      if (!(name in allValues)) {
        // FIX: Updated isEmployed default check to new field name pattern
        if (/^employmentCoApplicant\d+isEmployed$/.test(name)) {
          allValues[name] = "";
        } else if (/^addressCoApplicant\d+currentsameAsPermanent$/.test(name)) {
          allValues[name] = true;
        } else {
          allValues[name] = "";
        }
      } else if (/^addressCoApplicant\d+currentsameAsPermanent$/.test(name)) {
        allValues[name] = allValues[name] === true;
      }
    });

    if (currentFieldNames.includes(basicField("1", "PayParty"))) {
      allValues[basicField("1", "PayParty")] = "Yes";
    }

    formikRef.current.setValues(allValues, false);
  }, [allPageFields]);

  useEffect(() => {
    if (!Array.isArray(mergedCoApplicantFields) || mergedCoApplicantFields.length === 0) return;

    const basicFieldMap = new Map<string, any[]>();
    mergedCoApplicantFields.forEach((field: any) => {
      const match = String(field?.name || "").match(BASIC_REGEX);
      if (!match) return;
      const coAppKey = `CoApplicant${match[1]}`;
      const existingFields = basicFieldMap.get(coAppKey) || [];
      existingFields.push(field);
      basicFieldMap.set(coAppKey, existingFields);
    });

    basicFieldMap.forEach((basicFields, coAppKey) => {
      const coAppNum = coAppKey.replace("CoApplicant", "");
      const requiredBasicFields = basicFields.filter((field: any) => {
        if (!isOtherRelationFieldRequired(field)) return false;
        if (isFirstCoApplicantPayPartyField(String(field?.name || ""))) {
          return false;
        }
        return !/^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified|AadhaarNumber)$/.test(
          String(field?.name || ""),
        );
      });

      const hasEmptyRequired = requiredBasicFields.some((field: any) => {
        const value = formValues?.[field.name];
        return value === undefined || value === null || value === "";
      });

      const isPanVerified =
        formValues?.[basicField(coAppNum, "IsPanVerified")] === true;
      const mobileValue = String(
        formValues?.[basicField(coAppNum, "PhoneNumber")] || "",
      );
      const emailValue = String(
        formValues?.[basicField(coAppNum, "Email")] || "",
      );
      const isMobileValid = mobileValue && mobileValue?.toString()?.replace(/\D/g, "").length === 10;
      const isEmailValid = JOURNEY_EMAIL_REGEX.test(emailValue);

      const isBasicComplete =
        requiredBasicFields.length > 0 &&
        !hasEmptyRequired &&
        isPanVerified &&
        isMobileValid &&
        isEmailValid;

      // Only ever SET this flag here, never clear it reactively: isBasicComplete
      // is derived from live Formik values, which can transiently read as
      // "incomplete" for a co-applicant that was already genuinely saved
      // (e.g. right after adding one via AddNewCoApplicantModal, before this
      // page's own formValues/mergedCoApplicantFields have re-synced from the
      // fresh fetch). Clearing here wiped out real, saved co-applicants —
      // clearing must stay an explicit action tied to an actual delete (see
      // removeCoApplicantFields), matching CoApplicantDetailsSection.tsx.
      if (isBasicComplete && !savedBasicCoApplicants?.[coAppKey]) {
        dispatch(setSavedBasicForCoApplicant({ coAppKey, value: true }));
      }
    });
  }, [dispatch, mergedCoApplicantFields, formValues, savedBasicCoApplicants]);

  // Default addressCoApplicant{N}currentsameAsPermanent to true if unset
  useEffect(() => {
    if (formikRef?.current) {
      mergedCoApplicantFields.forEach((field: any) => {
        if (/^addressCoApplicant\d+(current)?sameAsPermanent$/.test(field?.name)) {
          const value = formikRef?.current?.values?.[field.name];
          if (typeof value === "undefined" || value === "" || value === null) {
            formikRef?.current?.setFieldValue(field.name, true, false);
          }
        }
      });
    }
  }, [mergedCoApplicantFields]);

  useEffect(() => {
    let isMounted = true;

    const fetchCoApplicantMaritalStatuses = async () => {
      const optionsFromDropdown = dropdownData?.maritalStatuses ?? [];
      if (Array.isArray(optionsFromDropdown) && optionsFromDropdown.length > 0) {
        if (isMounted) {
          setCoApplicantMaritalStatusOptions(optionsFromDropdown);
        }
        return;
      }

      try {
        const response = await dropdownService.getMaritalStatusMaster();
        const maritalStatusOptions = (response?.data || [])
          .map((item: any) => ({
            value: String(item?.maritalStatusCode ?? ""),
            label: String(
              item?.maritalStatusName ?? item?.maritalStatusDesc ?? "",
            ),
          }))
          .filter((opt: any) => opt.value && opt.label);

        if (isMounted) {
          setCoApplicantMaritalStatusOptions(maritalStatusOptions);
        }
      } catch (error) {
        console.error(
          "Error fetching co-applicant marital status master:",
          error,
        );
      }
    };

    fetchCoApplicantMaritalStatuses();

    return () => {
      isMounted = false;
    };
  }, [dropdownData?.maritalStatuses]);

  useEffect(() => {
    let isMounted = true;

    const fetchCoApplicantCountries = async () => {
      const optionsFromDropdown = dropdownData?.countries ?? [];
      if (Array.isArray(optionsFromDropdown) && optionsFromDropdown.length > 0) {
        if (isMounted) {
          setCoApplicantCountryOptions(optionsFromDropdown);
        }
        return;
      }

      try {
        const response = await dropdownService.getCountries();
        const countries = (response?.data || [])
          .map((item: any) => ({
            value: String(item?.countryId ?? ""),
            label: String(item?.countryDesc ?? ""),
          }))
          .filter((opt: any) => opt.value && opt.label);

        if (isMounted) {
          setCoApplicantCountryOptions(countries);
        }
      } catch (error) {
        console.error("Error fetching co-applicant countries:", error);
      }
    };

    fetchCoApplicantCountries();

    return () => {
      isMounted = false;
    };
  }, [dropdownData?.countries]);

  useEffect(() => {
    let isMounted = true;
    const fetchNationalities = async () => {
      try {
        const fetched = await dropdownService.getNationalityList();
        if (isMounted && fetched.length > 0) {
          setCoApplicantNationalityOptions(fetched);
          // Set default nationality to 'Indian' for each co-applicant if not already set
          mergedCoApplicantFields.forEach((field: any) => {
            if (/^basicCoApplicant\d+Nationality$/.test(field.name) || /naionality/.test(field.name.toLowerCase())) {
              const fieldName = field.name;
              const currentValue = formikRef?.current?.values?.[fieldName];
              if (!currentValue || (typeof currentValue === "object" && !currentValue.value && !currentValue.label)) {
                const indiaOption = fetched.find(opt => opt.label?.toLowerCase() === "indian" || opt.value?.toLowerCase() === "indian" || opt.value?.toLowerCase() === "in");
                if (indiaOption) {
                  formikRef?.current?.setFieldValue(fieldName, indiaOption, false);
                }
              }
            }
          });
        }
      } catch (error) {
        console.error("Error fetching co-applicant nationalities:", error);
      }
    };
    fetchNationalities();
    return () => { isMounted = false; };
  }, [mergedCoApplicantFields, formikRef]);
   
  // // ── Fetch Address Types, Property Types, Property Sub Types ───────────────
  // useEffect(() => {
  //   let isMounted = true;
  //   dropdownService.getAddressTypes().then((data) => {
  //     if (isMounted && data.length > 0) setAddressTypeOptions(data);
  //   }).catch(() => {});
  //   dropdownService.getPropertyTypes().then((data) => {
  //     if (isMounted && data.length > 0) setPropertyTypeOptions(data);
  //   }).catch(() => {});
  //   dropdownService.getPropertySubTypes().then((data) => {
  //     if (isMounted && data.length > 0) setPropertySubTypeOptions(data);
  //   }).catch(() => {});
  //   return () => { isMounted = false; };
  // }, []);

  // // When property type changes, re-fetch sub-types
  // useEffect(() => {
  //   if (!selectedPropertyType) return;
  //   let isMounted = true;
  //   dropdownService.getPropertySubTypes(selectedPropertyType).then((data) => {
  //     if (isMounted) setPropertySubTypeOptions(data);
  //   }).catch(() => {});
  //   return () => { isMounted = false; };
  // }, [selectedPropertyType]);

  const enrichCoApplicantFieldOptions = useCallback(
    (field: any) => {
      const fieldName = String(field?.name || "");
      const fieldLabel = String(field?.label || "").toLowerCase();

      if (/^basicCoApplicant\d+MaritalStatus$/.test(fieldName)) {
        return {
          ...field,
          options: { options: maritalStatusOptions },
        };
      }

      if (/^basicCoApplicant\d+Nationality$/.test(fieldName) || /naionality/.test(fieldName.toLowerCase())) {
        return {
          ...field,
          options: { options: nationalityOptions },
        };
      }

      if (
        /^basicCoApplicant\d+CountryOfBirth$/.test(fieldName) ||
        /country/.test(fieldLabel) ||
        /^addressCoApplicant\d+(permanent|current)Country$/.test(fieldName) ||
        /^employmentCoApplicant\d+companyCountry$/.test(fieldName)
      ) {
        return {
          ...field,
          options: { options: countryOptions },
        };
      }

      return field;
    },
    [countryOptions, maritalStatusOptions, nationalityOptions],
  );

  const isFilled = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value).trim() !== "";
  };

  const isCoApplicantPayPartyField = (fieldName: string): boolean =>
    /^basicCoApplicant\d+PayParty$/.test(fieldName);

  const getOtherRelationFieldMatch = (fieldName: string) =>
    String(fieldName || "").match(
      /^basicCoApplicant(\d+)(OtherRelationWithStudent|OtherRelationship|RelationValue)$/,
    );

  const isOtherRelationFieldVisible = (coAppNum: string | number): boolean =>
    formValues?.[basicField(coAppNum, "RelationWithStudent")] === "OTHERS";

  const isOtherRelationFieldRequired = (field: any): boolean => {
    const match = getOtherRelationFieldMatch(field?.name || "");
    if (!match) {
      return Boolean(field?.required);
    }
    return isOtherRelationFieldVisible(match[1]);
  };

  const isFirstCoApplicantPayPartyField = (fieldName: string): boolean =>
    isCoApplicantPayPartyField(fieldName) &&
    fieldName === basicField("1", "PayParty");

  const getCompletion = (): string => {
    const allRelevantFields = allPageFields.filter((f: any) => {
      if (f.required === false) return false;
      if (f.type === "hidden") return false;
      if (/ScoreType$/.test(f.name)) return false;
      if (isFirstCoApplicantPayPartyField(String(f?.name || ""))) return false;

      // Exclude employment sub-fields for co-applicants who selected "No"
      const empMatch = String(f?.name || "").match(/^employmentCoApplicant(\d+)(.+)$/);
      if (empMatch) {
        const coAppNum = empMatch[1];
        const suffix = empMatch[2];
        // Always include the isEmployed radio field itself
        if (suffix === "isEmployed" || suffix === "isEmployed") return true;
        // If this co-applicant selected "No", exclude all other employment fields
        const isEmployedFieldName =
          allPageFields.find((fld: any) =>
            new RegExp(`^employmentCoApplicant${coAppNum}(isEmployed|isEmployed)$`).test(String(fld?.name || ""))
          )?.name || `employmentCoApplicant${coAppNum}isEmployed`;
        const isEmployedValue = formValues?.[isEmployedFieldName];
        if (isEmployedValue === "No") return false;
        const employmentTypeFieldName = empField(coAppNum, "employmentType");
        const selectedEmploymentType = getAutocompleteOptionValue(
          formValues?.[employmentTypeFieldName],
        );
        if (
          isEmployedValue === "Yes" &&
          !selectedEmploymentType &&
          String(f?.name || "") !== employmentTypeFieldName
        ) {
          return false;
        }
      }

      return true;
    });
    const total = allRelevantFields.length;
    const filled = allRelevantFields.filter((f: any) =>
      isFilled(formValues?.[f.name]),
    ).length;
    return `${filled} / ${total}`;
  };

  const prevValuesRef: any = useRef({});
  const prevSelectedCoApplicantIndexRef = useRef<number | null>(null);
  const prevSameAsValueRef = useRef<string | undefined>(undefined);
  const skipResetAfterSameAsRef = useRef(false);

    useEffect(() => {
      if (formikRef?.current) {
        const values = formikRef.current.values || {}
        const i = activeCoApplicantNum;
          if(values[`addressCoApplicant${i}SameAs`] === "Applicant") {
            const permanentOwnershipStatus = values.permanentOwnershipStatus;
            const permanentYearsInCurrentAddress = values.permanentYearsInCurrentAddress;
            const permanentYearsInCurrentCity = values.permanentYearsInCurrentCity;
            const permanentAddressLine1 = values.permanentAddressLine1;
            const permanentAddressLine2 = values.permanentAddressLine2;
            const permanentAddressLine3 = values.permanentAddressLine3;
            const permanentPincode = values.permanentPincode;
            const permanentCity = values.permanentCity;
            const permanentState = values.permanentState;
            const permanentCountry = values.permanentCountry;
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentAddressLine1`, permanentAddressLine1);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentAddressLine2`, permanentAddressLine2);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentAddressLine3`, permanentAddressLine3);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentPincode`, permanentPincode);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentCity`, permanentCity);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentState`, permanentState);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentCountry`, permanentCountry);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentOwnershipStatus`, permanentOwnershipStatus);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentYearsInCurrentAddress`, permanentYearsInCurrentAddress);
            formikRef.current.setFieldValue(`addressCoApplicant${i}permanentYearsInCurrentCity`, permanentYearsInCurrentCity);
  
            const currentAddressLine1 = values.currentAddressLine1;
            const currentAddressLine2 = values.currentAddressLine2;
            const currentAddressLine3 = values.currentAddressLine3;
            const currentCity = values.currentCity;
            const currentCountry = values.currentCountry;
            const currentOwnershipStatus = values.currentOwnershipStatus;
            const currentPincode = values.currentPincode;
            const currentState = values.currentState;
            const currentYearsInCurrentAddress = values.currentYearsInCurrentAddress;
            const currentYearsInCurrentCity = values.currentYearsInCurrentCity;
  
            const currentsameAsPermanent = values.currentsameAsPermanent;
            const addressCoApplicantcurrentsameAsPermanent = values[`addressCoApplicant${i}currentsameAsPermanent`];
  
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentAddressLine1`, currentAddressLine1);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentAddressLine2`, currentAddressLine2);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentAddressLine3`, currentAddressLine3);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentCity`, currentCity);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentCountry`, currentCountry);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentOwnershipStatus`, currentOwnershipStatus);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentPincode`, currentPincode);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentState`, currentState);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentYearsInCurrentAddress`, currentYearsInCurrentAddress);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentYearsInCurrentCity`, currentYearsInCurrentCity);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentsameAsPermanent`, currentsameAsPermanent);
            formikRef.current.setFieldValue(`addressCoApplicant${i}currentsameAsPermanent`, addressCoApplicantcurrentsameAsPermanent);
          } else {
            const addressCoApplicantSameAs = values[`addressCoApplicant${i}SameAs`]; 
            if(addressCoApplicantSameAs) {
              const coAppNumber = Number(addressCoApplicantSameAs.match(/\d+/)[0]);
  
              
  
              const addressCoAppPermanentOwnershipStatus = values[`addressCoApplicant${coAppNumber}permanentOwnershipStatus`];
              const addressCoAppPermanentYearsInCurrentAddress = values[`addressCoApplicant${coAppNumber}permanentYearsInCurrentAddress`];
              const addressCoAppPermanentYearsInCurrentCity = values[`addressCoApplicant${coAppNumber}permanentYearsInCurrentCity`];
              const addressCoAppPermanentAddressLine1 = values[`addressCoApplicant${coAppNumber}permanentAddressLine1`];
              const addressCoAppPermanentAddressLine2 = values[`addressCoApplicant${coAppNumber}permanentAddressLine2`];
              const addressCoAppPermanentAddressLine3 = values[`addressCoApplicant${coAppNumber}permanentAddressLine3`];
              const addressCoAppPermanentPincode = values[`addressCoApplicant${coAppNumber}permanentPincode`];
              const addressCoAppPermanentCity = values[`addressCoApplicant${coAppNumber}permanentCity`];
              const addressCoAppPermanentState = values[`addressCoApplicant${coAppNumber}permanentState`];
              const addressCoAppPermanentCountry = values[`addressCoApplicant${coAppNumber}permanentCountry`];
  
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentAddressLine1`, addressCoAppPermanentAddressLine1);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentAddressLine2`, addressCoAppPermanentAddressLine2);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentAddressLine3`, addressCoAppPermanentAddressLine3);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentPincode`, addressCoAppPermanentPincode);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentCity`, addressCoAppPermanentCity);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentState`, addressCoAppPermanentState);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentCountry`, addressCoAppPermanentCountry);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentOwnershipStatus`, addressCoAppPermanentOwnershipStatus);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentYearsInCurrentAddress`, addressCoAppPermanentYearsInCurrentAddress);
              formikRef.current.setFieldValue(`addressCoApplicant${i}permanentYearsInCurrentCity`, addressCoAppPermanentYearsInCurrentCity);
  
              const addressCoAppCurrentAddressLine1 = values[`addressCoApplicant${coAppNumber}currentAddressLine1`];
              const addressCoAppCurrentAddressLine2 = values[`addressCoApplicant${coAppNumber}currentAddressLine2`];
              const addressCoAppCurrentAddressLine3 = values[`addressCoApplicant${coAppNumber}currentAddressLine3`];
              const addressCoAppCurrentCity = values[`addressCoApplicant${coAppNumber}currentCity`];
              const addressCoAppCurrentCountry = values[`addressCoApplicant${coAppNumber}currentCountry`];
              const addressCoAppCurrentOwnershipStatus = values[`addressCoApplicant${coAppNumber}currentOwnershipStatus`];
              const addressCoAppCurrentPincode = values[`addressCoApplicant${coAppNumber}currentPincode`];
              const addressCoAppCurrentState = values[`addressCoApplicant${coAppNumber}currentState`];
              const addressCoAppCurrentYearsInCurrentAddress = values[`addressCoApplicant${coAppNumber}currentYearsInCurrentAddress`];
              const addressCoAppCurrentYearsInCurrentCity = values[`addressCoApplicant${coAppNumber}currentYearsInCurrentCity`];
              const addressCoAppCurrentsameAsPermanent = values[`addressCoApplicant${coAppNumber}currentsameAsPermanent`];
  
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentAddressLine1`, addressCoAppCurrentAddressLine1);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentAddressLine2`, addressCoAppCurrentAddressLine2);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentAddressLine3`, addressCoAppCurrentAddressLine3);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentCity`, addressCoAppCurrentCity);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentCountry`, addressCoAppCurrentCountry);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentOwnershipStatus`, addressCoAppCurrentOwnershipStatus);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentPincode`, addressCoAppCurrentPincode);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentState`, addressCoAppCurrentState);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentYearsInCurrentAddress`, addressCoAppCurrentYearsInCurrentAddress);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentYearsInCurrentCity`, addressCoAppCurrentYearsInCurrentCity);
              formikRef.current.setFieldValue(`addressCoApplicant${i}currentsameAsPermanent`, addressCoAppCurrentsameAsPermanent);
            }
        }
      }
    }, [formikRef?.current?.values[`addressCoApplicant${activeCoApplicantNum}SameAs`]]);
  
    useEffect(() => {
      if (!formikRef?.current || !activeCoApplicantNum) return;
  
      const { values, setFieldValue } = formikRef.current;
      const prefix = `addressCoApplicant${activeCoApplicantNum}`;
      const sameAsKey = `${prefix}SameAs`;
  
      const currentSameAsValue = values[sameAsKey];
      const previousSameAsValue = prevSameAsValueRef.current;
  
      const targetFields = targetAddressFields(prefix);
      const targetFieldsChanged = targetFields.some((field) => {
        return prevValuesRef.current[field] !== values[field];
      });
  
      const sameAsChanged = previousSameAsValue !== currentSameAsValue;
      const selectedIndexChanged = prevSelectedCoApplicantIndexRef.current !== Number(activeCoApplicantNum);
  
      if (selectedIndexChanged) {
        prevSelectedCoApplicantIndexRef.current = Number(activeCoApplicantNum);
        prevValuesRef.current = { ...values };
        prevSameAsValueRef.current = currentSameAsValue;
        skipResetAfterSameAsRef.current = false;
        return;
      }
  
      if (sameAsChanged) {
        skipResetAfterSameAsRef.current = true;
      }
  
      if (targetFieldsChanged && !skipResetAfterSameAsRef.current && values[sameAsKey]) {
        setFieldValue(sameAsKey, "");
      }
  
      if (!sameAsChanged) {
        skipResetAfterSameAsRef.current = false;
      }
  
      prevValuesRef.current = { ...values };
      prevSameAsValueRef.current = currentSameAsValue;
    }, [formikRef?.current?.values, activeCoApplicantNum]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Default collateral to 'No' */}
      {(() => {
        if (formikRef?.current && formikRef.current.values) {
          if (
            formikRef.current.values["collateral"] === undefined ||
            formikRef.current.values["collateral"] === ""
          ) {
            formikRef.current.setFieldValue("collateral", "No", false);
          }
        }
      })()}

      {/* Loan Amount & Collateral */}
      <Grid container spacing={2} m={0}>
        <Grid item xs={12} md={4}>
          {fields
            .filter((f: any) => f.name === "LoanDetailsLoanAmount")
            .map((field: any) => (
              <React.Fragment key={field.name}>
                {renderField(field)}
              </React.Fragment>
            ))}
        </Grid>
        <Grid item xs={12} md={4}>
          {fields
            .filter((f: any) => f.name === "collateral")
            .map((field: any) => (
              <React.Fragment key={field.name}>
                {renderField(field)}
              </React.Fragment>
            ))}
        </Grid>
        <Grid item xs={12}>
          {formikRef?.current?.values?.["collateral"] === "Yes" && (
            <Grid container spacing={2}>
              {fields
                .filter(
                  (f: any) =>
                    f.name === "collateralType" ||
                    f.name === "collateralAmount",
                )
                .map((field: any) => (
                  <Grid item xs={12} sm={4} key={field.name}>
                    {renderField({ ...field, required: true })}
                  </Grid>
                ))}

              {fields
                .filter((f: any) =>
                  f.name === "collateralAddressType" ||
                  f.name === "collateralPropertyType" ||
                  f.name === "collateralPropertySubType",
                )
                .map((field: any) => {
                  let enriched = { ...field };
                  if (field.name === "collateralAddressType") {
                    enriched = { ...field, options: { options: addressTypeOptions } };
                  } else if (field.name === "collateralPropertyType") {
                    enriched = { ...field, options: { options: propertyTypeOptions } };
                  } else if (field.name === "collateralPropertySubType") {
                    enriched = { ...field, options: { options: propertySubTypeOptions } };
                  }
                  return (
                    <Grid item xs={12} sm={4} key={field.name}>
                      {renderField(enriched)}
                    </Grid>
                  );
                })}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Co-Applicant header */}
      <Grid container spacing={2} justifyContent="space-between" mx={0} mt={1}>
        <Grid item xs={12}>
          <SectionHeader
            icon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  bgcolor: "#e5feffff",
                }}
              >
                <img
                  src={idIcon}
                  alt="icon"
                  style={{
                    width: 28,
                    height: 28,
                  }}
                />
              </Box>
            }
            title={"Co-Applicant Additional Details"}
            subtitle={"Additional Details"}
            completion={getCompletion()}
          />
        </Grid>
      </Grid>

      {pageType != "SUMMARY" && (
        <Grid
          item
          xs={12}
          sx={{
            mb: 0,
            p: 1,
            bgcolor: "#fff3cf",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center", 
            ml: 2,
            mr: 2,
            mt:2
          }}
        >
          <Box
            sx={{
            pl: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            }}
          >
            <Typography color="textSecondary">
              Please add at least 2 co-applicants to proceed with your application
            </Typography>
         </Box>                               
        </Grid>
      //   <Grid
      //   item
      //   xs={12}
      //   sx={{
      //    mb: 1,
      //    p: 1,
      //    backgroundColor: "#FFE38A",
      //    borderRadius: "8px",
      //    display: "flex",
      //    alignItems: "center", 
      //    ml: 2,
      //    mt:2
      //   }}
      //   >
      //     <Typography
      //       variant="subtitle1"
      //       sx={{
      //         fontWeight: 400,
      //         fontSize: "15px",
      //         color: "#1F2233",
      //       }}
      //       >
      //       Please add at least 2 co-applicants to proceed with your application
      //     </Typography>
      // </Grid>
      )}

      {/* Add More (desktop) */}
      <Grid container justifyContent="end" sx={{ mx: 2, mt: pageType != "SUMMARY" ? 1 : 4 }}>
        {!isXsOrSm && numberOfCoApp < 9 && (
          <Grid textAlign="right">
            <Button
              sx={{
                textTransform: "none",
                fontFamily: "Jost, sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#33c2c4",
                borderColor: "#33c2c4",
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: "#33c2c4",
                  color: "#33c2c4",
                },
              }}
              onClick={addCoApplicantFields}
              disabled={isLoading || disableAddMoreCoApplicant}
            >
              <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
              Add More
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Add Co-Applicant (mobile) */}
      {isXsOrSm && numberOfCoApp < 9 && (
        <Grid item xs={12}>
          <Button
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#33c2c4",
              border: "1px solid #33c2c4",
              textAlign: "center",
              width: "100%",
            }}
            onClick={addCoApplicantFields}
            disabled={isLoading || disableAddMoreCoApplicant}
          >
            <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
            Add Co-Applicant
          </Button>
        </Grid>
      )}

      {/* ── Co-applicant accordion list ─────────────────────────────────── */}
      {(() => {
        // ── Collapsible state ─────────────────────────────────────────────
        React.useEffect(() => {
          if (
            expanded &&
            typeof expanded === "string" &&
            !coApplicantKeys.includes(expanded)
          ) {
            const fallback =
              coApplicantKeys[coApplicantKeys.length - 1] || "CoApplicant1";
            setExpanded(fallback);
            if (typeof window !== "undefined") {
              localStorage.setItem("coAppExpandedKey", fallback);
            }
            dispatch(setLoanDetailCoApplicantCollapseValue(fallback));
          }
        }, [coApplicantKeys, expanded]);

        const [tabEnabled, setTabEnabled] = React.useState<
          Record<string, boolean[]>
        >({});

        React.useEffect(() => {
          // Recompute for every co-applicant on every values change — the
          // `if (!tabEnabled[coAppKey])` guard this used to have only ever
          // computed a co-applicant's tab-enabled state ONCE (whenever it
          // first appeared, typically while its fields were still empty),
          // then never again. So Address/Employment stayed disabled forever
          // even after the user filled in every required field, since
          // nothing ever re-ran the check.
          setTabEnabled((prev) => {
            const next = { ...prev };
            coApplicantKeys.forEach((coAppKey) => {
              const tabStates = [0, 1, 2].map((tabIdx) => {
                const tabFieldsArr = [
                  coApplicantFieldMap[coAppKey].basic,
                  coApplicantFieldMap[coAppKey].address,
                  coApplicantFieldMap[coAppKey].employment,
                ];
                const requiredFields = tabFieldsArr[tabIdx].filter((f) => {
                  // Employment tab: mirror the same exclusions the Save &
                  // Next disabled-check below uses. This used to only ever
                  // exclude the employmentType field itself when isEmployed
                  // is "No" — every OTHER employment sub-field (company
                  // name/address, amounts, etc.) was still being required
                  // even though none of them are shown/fillable in that
                  // case, so the tab could never actually complete and
                  // stayed disabled forever.
                  const empMatch = f.name.match(/^employmentCoApplicant(\d+)/);
                  if (empMatch) {
                    const coAppNum = empMatch[1];
                    const isEmployedFieldName = `employmentCoApplicant${coAppNum}isEmployed`;
                    const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
                    const isEmployed =
                      formikRef?.current?.values?.[isEmployedFieldName];

                    if (isEmployed === "No") {
                      return f.name === isEmployedFieldName;
                    }

                    const employmentTypeValue =
                      formikRef?.current?.values?.[employmentTypeFieldName];
                    if (
                      isEmployed === "Yes" &&
                      (employmentTypeValue === "Others" ||
                        employmentTypeValue === "Farmer") &&
                      f.name !== employmentTypeFieldName &&
                      f.name !== empField(coAppNum, "monthlyObligation") &&
                      f.name !== empField(coAppNum, "monthlyIncome")
                    ) {
                      return false;
                    }
                  }
                  return isOtherRelationFieldRequired(f);
                });
                return requiredFields.every((f) => {
                  const val = formikRef?.current?.values?.[f.name];
                  return val !== undefined && val !== null && val !== "";
                });
              });
              // Always enable the first tab; the others only unlock once
              // their required fields are actually filled (mirrors
              // CoApplicantDetailsSection.tsx) — previously this hardcoded
              // [true, true, true] regardless of tabStates, which is why
              // every tab was always clickable.
              next[coAppKey] = [true, ...tabStates.slice(1)];
            });
            return next;
          });
        }, [coApplicantKeys, formikRef?.current?.values]);

        // Keep "Add More" in sync with the last co-applicant's actual
        // completion state (tabEnabled, computed above from current form
        // values) instead of only a one-off dispatch after a save action.
        // disableAddMoreCoApplicant defaults to `true` in Redux, so on a
        // fresh page load/re-login for a lead whose co-applicants are
        // already fully filled in, nothing was re-evaluating it against the
        // restored data — the button stayed disabled even though the data
        // was actually complete.
        React.useEffect(() => {
          if (coApplicantKeys.length === 0) return;
          const lastCoAppKey = coApplicantKeys[coApplicantKeys.length - 1];
          const lastCoApplicantTabs = tabEnabled[lastCoAppKey];
          if (!lastCoApplicantTabs) return;
          const isLastCoApplicantComplete = lastCoApplicantTabs.every(Boolean);
          if (disableAddMoreCoApplicant === !isLastCoApplicantComplete) return;
          dispatch(setDisableAddMoreCoApplicant(!isLastCoApplicantComplete));
        }, [tabEnabled, coApplicantKeys, disableAddMoreCoApplicant, dispatch]);

        const handleAccordionChange = (panel: string) => () => {
          const nextExpanded = expanded === panel ? false : panel;
          if (nextExpanded && typeof window !== "undefined") {
            localStorage.setItem("coAppExpandedKey", nextExpanded as string);
          } else if (typeof window !== "undefined") {
            localStorage.removeItem("coAppExpandedKey");
          }
          dispatch(
            setLoanDetailCoApplicantCollapseValue(
              typeof nextExpanded === "string" ? nextExpanded : panel,
            ),
          );
          setExpanded(nextExpanded);
        };

        const handleZipCode = (coAppKey: any) => {
          const coAppNum = coAppKey.split("CoApplicant")?.[1];
          if (!coAppNum) return;

          const zipCodePermanentVal = String(
            formikRef?.current?.values?.[permField(coAppNum, "Pincode")] || "",
          );
          const zipCodeCurrentVal = String(
            formikRef?.current?.values?.[currField(coAppNum, "Pincode")] || "",
          );

          invokeZipCodeLookup(zipCodePermanentVal, coAppNum, "permanent");
          invokeZipCodeLookup(zipCodeCurrentVal, coAppNum, "current");
        };
        const  getCoApplicantStatusIndex=(formValues: Record<string, any>, coApplicantIndex: number|any,coAppKey:any) =>{
          if (!formValues || typeof formValues !== 'object') {
            return 0;
          }

          // 1. Check if any basic fields are filled for this co-applicant index
        
            const filledCoAppFieldsCount = Object.keys(formValues).filter((key) => {
              const isTargetCoApp = key.startsWith(`basicCoApplicant${coApplicantIndex}`);
              const val = formValues[key];
              return isTargetCoApp && val !== null && val !== undefined && String(val).trim() !== "";
            }).length;

            // Step is complete ONLY when all 13 fields are filled
            const hasBasicFields = filledCoAppFieldsCount >= 13;
        

          // 2. Check if any address fields (current or permanent) are filled for this co-applicant index
          const hasAddressFields = Object.keys(formValues).some((key) => {
            const isTargetCoApp = key.startsWith(`addressCoApplicant${coApplicantIndex}`);
            const val = formValues[key];
            // Exclude the "sameAs" boolean checks so we only count actual address inputs
            const isNotCheckbox = !key.toLowerCase().includes("sameas"); 
            return isTargetCoApp && isNotCheckbox && val !== null && val !== undefined && String(val).trim() !== "";
          });
          // 3. Apply your index mapping rules
          if (hasAddressFields) {
            return 2;
          }
          
          if (hasBasicFields) {
            setTabIndex((prev: any) => ({ ...prev, [coAppKey]: 1 }));
            return 1;
          }
          else{
            setTabIndex((prev: any) => ({ ...prev, [coAppKey]: 0 }));
            return 0;
          }
          return 0;
        }
        const handleTabChange = (coAppKey: string, newIndex: number) => {
      
          if (newIndex === 1) handleZipCode(coAppKey);
          if (tabEnabled[coAppKey]?.[newIndex]) {
            setTabIndex((prev: any) => ({ ...prev, [coAppKey]: newIndex }));
          }
        };
        // ── Save & Next handler ───────────────────────────────────────────
        const handleTabSave = (
          coAppKey: string,
          tabIdx: number,
          isLast: boolean,
        ) => {
          (async () => {
            setIsSaveNextLoading(true);
            const coAppNum = coAppKey.replace("CoApplicant", "");
            const values = formikRef?.current?.values || {};
            const coApplicantGroup = coApplicantFieldMap[coAppKey];
            const formDataPayload: any[] = [];
            (coApplicantGroup?.allFields || []).forEach((field: any) => {
              // FIX 5: Updated to match both new employment prefixes
              const isBelongsToThisCoApp =
                field.name.startsWith(`basicCoApplicant${coAppNum}`) ||
                field.name.startsWith(`addressCoApplicant${coAppNum}`) ||
                field.name.startsWith(`employmentCoApplicant${coAppNum}`);

              if (isBelongsToThisCoApp) {
                const rawFieldValue = values[field.name];
                const isBirthCityField = /city.*birth|birth.*city/i.test(
                  String(field?.label || "") + String(field?.name || ""),
                );
                const isNationalityField = /^basicCoApplicant\d+Nationality$/.test(
                  String(field?.name || ""),
                );
                const isMaritalStatusField = /^basicCoApplicant\d+MaritalStatus$/.test(
                  String(field?.name || ""),
                );
                const isCountryOfBirthField = /^basicCoApplicant\d+CountryOfBirth$/.test(
                  String(field?.name || ""),
                );
                // save-coapplicant (local API) always expects the display
                // name, never the master-data code — resolve against the
                // matching options list regardless of whether Formik/Redux
                // currently holds the code or the label for this field, so
                // it's consistent whichever journey (DIY/Summary) set it.
                let normalizedFieldValue: any;
                if (isMaritalStatusField) {
                  normalizedFieldValue =
                    resolveAutocompleteOption(rawFieldValue, maritalStatusOptions)?.label ??
                    getAutocompleteOptionValue(rawFieldValue);
                } else if (isCountryOfBirthField) {
                  normalizedFieldValue =
                    resolveAutocompleteOption(rawFieldValue, countryOptions)?.label ??
                    getAutocompleteOptionValue(rawFieldValue);
                } else if (isNationalityField) {
                  normalizedFieldValue =
                    resolveAutocompleteOption(rawFieldValue, nationalityOptions)?.label ??
                    (rawFieldValue && typeof rawFieldValue === "object"
                      ? ("label" in rawFieldValue
                          ? rawFieldValue.label
                          : "value" in rawFieldValue
                            ? rawFieldValue.value
                            : rawFieldValue)
                      : rawFieldValue);
                } else if (isBirthCityField) {
                  normalizedFieldValue =
                    rawFieldValue && typeof rawFieldValue === "object"
                      ? ("label" in rawFieldValue
                          ? rawFieldValue.label
                          : "value" in rawFieldValue
                            ? rawFieldValue.value
                            : rawFieldValue)
                      : rawFieldValue;
                } else if (/^employmentCoApplicant\d+designation$/i.test(String(field?.name || ""))) {
                  normalizedFieldValue = getMasterOptionName(rawFieldValue, dropdownData?.designations);
                } else {
                  normalizedFieldValue = rawFieldValue;
                }
                formDataPayload.push({
                  formFieldId: field.id,
                  fieldKey: field.name,
                  fieldValue: normalizedFieldValue ?? "",
                });
              }
            });

            const mobileNumber =
              values[basicField(coAppNum, "PhoneNumber")] || "";
            const email = values[basicField(coAppNum, "Email")] || "";
            const coApplicantType = `Co-Applicant ${coAppNum}`;

            const coAppIndex = String(Number(coAppNum) - 1);
            const aadhaarSeedingStatus =
              coApplicantVerification?.[coAppIndex]?.aadhaarSeedingStatus;
            const isAadhaarSeeded =
              aadhaarSeedingStatus === undefined ||
              aadhaarSeedingStatus === null ||
              aadhaarSeedingStatus === ""
                ? false
                : aadhaarSeedingStatus;

            let finalIsAadhaarSeeded = isAadhaarSeeded;
            if (finalIsAadhaarSeeded === "") {
              const payload = { trackingId, coApplicantType };
              const resp: any =
                await losServices.getCoApplicantDetails(payload);
              const statusFromApi = resp?.data?.data?.isAadhaarSeeded;
              finalIsAadhaarSeeded =
                statusFromApi === undefined ||
                statusFromApi === null ||
                statusFromApi === ""
                  ? ""
                  : statusFromApi;
            }

            try {
              const shouldAutoInvokeLeadFlow: boolean =
                checkIsLeadApiInvoked.ASSIGN_SM === false ||
                checkIsLeadApiInvoked.CREATE_LEAD === false ||
                checkIsLeadApiInvoked.LEAD_TO_APPLICATION === false;
              if (shouldAutoInvokeLeadFlow) {
                handleLeadCreationFlow(getLeadPayload, leadSequenceId);
              }

              dispatch(updateFormData(values));
              let section="basic";
              if(tabIdx===1){
                section="address";
              }else if(tabIdx===2){
                section="employment";
              }
              // idDto: resolved-ID mirror of the raw formDataPayload above,
              // for this save-coapplicant call into OUR OWN DB (not the
              // processio/LOS calls further down, which build their own
              // payloads independently). See buildCoApplicantSaveIdDto for
              // why: built entirely from what's already synchronously
              // available (dropdownData lookups, coApplicantZipCodeData) —
              // no new API calls, no reordering. Shared across
              // CoApplicantDetailsSection/CoApplicantAdditionalDetails/
              // FormEdit instead of each carrying its own copy.
              const birthCountryLabelForDto =
                resolveAutocompleteOption(
                  values[basicField(coAppNum, "CountryOfBirth")],
                  countryOptions,
                )?.label ||
                getAutocompleteOptionLabel(
                  values[basicField(coAppNum, "CountryOfBirth")],
                  countryOptions,
                );
              // birthCityId is mandatory downstream, so this needs to be
              // more than a cache-hit check: getCityOptionsForCountry can
              // come back non-empty yet still not contain the specific city
              // this co-applicant selected (e.g. a stale/partial cache from
              // an earlier country), which would silently resolve to blank
              // without ever attempting a fetch. Retry with a fresh fetch
              // whenever the cached list doesn't actually resolve this raw
              // city value — not just when it's empty — same
              // fetchCitiesByCountryState call the (unconditional,
              // same-values) Basic Details ID block further down already
              // makes for the LOS payload's own birthCityId.
              const rawBirthCityForDto = values[basicField(coAppNum, "CityOfBirth")];
              let cityOptionsForBirthForDto = getCityOptionsForCountry(
                birthCountryLabelForDto,
              );
              if (
                birthCountryLabelForDto &&
                !resolveBirthCityId(rawBirthCityForDto, cityOptionsForBirthForDto)
              ) {
                cityOptionsForBirthForDto = await fetchCitiesByCountryState(
                  birthCountryLabelForDto,
                );
              }
              const idDto = buildCoApplicantSaveIdDto({
                getField: (fieldSection, suffix) => {
                  switch (fieldSection) {
                    case "basic":
                      return values[basicField(coAppNum, suffix)];
                    case "permanent":
                      return values[permField(coAppNum, suffix)];
                    case "current":
                      return values[currField(coAppNum, suffix)];
                    case "employment":
                      return values?.[empField(coAppNum, suffix)];
                    case "company":
                      return values[companyField(coAppNum, `company${suffix}`)];
                    default:
                      return undefined;
                  }
                },
                stage: section as "basic" | "address" | "employment",
                countryOptions,
                maritalStatusOptions,
                nationalityOptions,
                cityOptionsForBirth: cityOptionsForBirthForDto,
                coApplicantZipCodeData,
                coAppKey,
                designations: dropdownData?.designations,
              });
              let res = await postsServices.addPost(
                "api/v1/applicant/save-coapplicant",
                {
                  trackingId,
                  mobileNumber,
                  coApplicantType,
                  email: email.toLowerCase(),
                  isAadhaarSeeded:
                    isAadhaarSeeded === "" ||
                    isAadhaarSeeded === null ||
                    isAadhaarSeeded === undefined
                      ? finalIsAadhaarSeeded
                      : isAadhaarSeeded,
                  formData: formDataPayload,
                  section:section,
                  idDto,
                },
              );
              setSnackbar({
                open: true,
                message: "Co-applicant details for the tab saved successfully.",
                severity: "success",
              });

                let page = "";
                let event = "";

                switch (tabIdx) {
                    case 0:
                      page = `Extended Journey_CoApplicant Additional Details_Basic (${coAppNum})`;
                      event = `${extendedJourneyPrefix}_APPADDDETAILS_BASIC_SAVE_PAGE12`;
                      break;
                    case 1:
                      page = `Extended Journey_CoApplicant Additional Details_Address (${coAppNum})`;
                      event = `${extendedJourneyPrefix}_APPADDDETAILS_ADDRESS_SAVE_PAGE12`;
                      break;
                    case 2:
                      page = `Extended Journey_CoApplicant Additional Details_Employment (${coAppNum})`;
                      event = `${extendedJourneyPrefix}_APPADDDETAILS_EMPLOY_SAVE_PAGE12`;
                      break;
                    default:
                      page = "";
                      event = "";
                }

                trackCustomerJourneyEvent(
                  event,
                  decrypt(userProfile?.id) ?? "",
                  trackingId,
                  {
                    label: event,
                    category: event,
                    action: event,
                    page: page,
                  }
                );
              
              const payload = { trackingId, coApplicantType };
              let applicantIdValue = "";
              const resp: any =
                await losServices.getCoApplicantDetails(payload);

              if (
                (tabIdx === 0 && res?.status === 200) ||
                res?.status === 201
              ) {
                dispatch(
                  setSavedBasicForCoApplicant({ coAppKey, value: true }),
                );
              }

              if (
                tabIdx === 2 &&
                (res?.status === 200 || res?.status === 201)
              ) {
                dispatch(
                  setSavedEmploymentForCoApplicant({ coAppKey, value: true }),
                );
              }

              if (tabIdx === 2 && resp?.status === 200) {
                setExpanded(false);
                if (typeof window !== "undefined") {
                  localStorage.removeItem("coAppExpandedKey");
                }
                dispatch(setLoanDetailCoApplicantCollapseValue(""));
                updateAddMoreStateFromCoApplicantResp(resp);
              }

              if (resp.status === 200)
                applicantIdValue = resp?.data?.data?.applicantId;

              if (!isLast) {
                setTabEnabled((prev) => {
                  const arr = prev[coAppKey]
                    ? [...prev[coAppKey]]
                    : [true, false, false];
                  arr[tabIdx + 1] = true;
                  return { ...prev, [coAppKey]: arr };
                });
                setTabIndex((prev: any) => ({
                  ...prev,
                  [coAppKey]: tabIdx + 1,
                }));
              }

              if (tabIdx === 0) handleZipCode(coAppKey);

              // Gender can be stored as "male" (dynamic form options), "Male"
              // (AddNewCoApplicantModal options) or a code like "M", so compare
              // case-insensitively against both name and code.
              const genderRaw = String(
                values[basicField(coAppNum, "Gender")] ?? "",
              ).trim().toLowerCase();
              const genderId =
                mapGender().find(
                  (item: any) =>
                    item.name?.toLowerCase() === genderRaw ||
                    item.code?.toLowerCase() === genderRaw,
                )?.code || "";
              const maritalStatusRaw =
                values[basicField(coAppNum, "MaritalStatus")];
              const maritalStatusOption = resolveAutocompleteOption(
                maritalStatusRaw,
                maritalStatusOptions,
              );
              const maritalStatusId = maritalStatusOption?.value || "";
              const maritalStatus = maritalStatusOption?.label || "";
              const nationalityRaw = values[basicField(coAppNum, "Nationality")];
              const nationalityOption = resolveAutocompleteOption(
                nationalityRaw,
                nationalityOptions,
              );
              const nationalityId = nationalityOption?.value || "";
              const nationalityName = nationalityOption?.label || "";
              const birthCountryRaw = values[basicField(coAppNum, "CountryOfBirth")];
              const birthCountryOption = resolveAutocompleteOption(
                birthCountryRaw,
                countryOptions,
              );
              const birthCountryId = birthCountryOption?.value || "";
              const birthCountryLabel =
                birthCountryOption?.label ||
                getAutocompleteOptionLabel(birthCountryRaw, countryOptions);
              let cityOptionsForBirth = getCityOptionsForCountry(birthCountryLabel);
              if (!cityOptionsForBirth.length && birthCountryLabel) {
                cityOptionsForBirth = await fetchCitiesByCountryState(
                  birthCountryLabel,
                );
              }
              const birthCityRaw = values[basicField(coAppNum, "CityOfBirth")];
              const birthCityId = resolveBirthCityId(
                birthCityRaw,
                cityOptionsForBirth,
              );

              // ── Basic Details LOS event ──
              if (tabIdx === 0 && res?.data?.leadId) {
                if (applicantIdValue) {
                  trackCustomerJourneyEvent(
                    `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                    isAuthenticated ? (decrypt(userProfile?.id) ?? "") : "",
                    trackingId,
                    {
                      label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Basic Details`,
                    },
                  );
                } else {
                  if (Number(applicationId) > 0) {
                    trackCustomerJourneyEvent(
                      `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                      isAuthenticated ? (decrypt(userProfile?.id) ?? "") : "",
                      trackingId,
                      {
                        label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_SAVE_PAGE10`,
                        page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Basic Details`,
                      },
                    );
                  }
                }
              }
              
            } catch (err) {
              console.error("Error saving co-applicant:", err);
            } finally {
              setIsSaveNextLoading(false);
              if (isXsOrSm) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }
          })();
        };

        // ── Render each co-applicant accordion ───────────────────────────
        return coApplicantKeys.map((coAppKey, idx) => {
          const group = coApplicantFieldMap[coAppKey];
          const coAppNum = coAppKey.replace("CoApplicant", "");
          //const currentTab = tabIndex[coAppKey] || 0;
          //  const currentTab=tabIndex[coAppKey] ?? getCoApplicantStatusIndex(formikRef?.current?.values,coAppNum,coAppKey) ?? 0;
          const currentTab=tabIndex[coAppKey] ?? 0;

          //console.log(getCoApplicantStatusIndex(formikRef?.current?.values,coAppNum),"testtest")
          const tabLabels = [
            "Basic Details",
            "Address Details",
            "Employment Details",
          ];

          const sortByFieldOrder = (arr: any[]) =>
            (arr || [])
              .slice()
              .sort(
                (a: any, b: any) => (a?.fieldOrder ?? 0) - (b?.fieldOrder ?? 0),
              );
          

          const tabFields = [
            sortByFieldOrder(group.basic).map(enrichCoApplicantFieldOptions),
            sortByFieldOrder(group.address).map(enrichCoApplicantFieldOptions),
            sortByFieldOrder(group.employment).map(enrichCoApplicantFieldOptions),
          ];
          
          return (
            <React.Fragment key={coAppKey}>
              <Grid xs={12} sx={{ pl: 2, mt: 3 }} className="border-none">
                <CollapsibleSection
                  title={`Co-Applicant ${idx + 1}`}
                  isExpanded={expanded === coAppKey}
                  onToggle={handleAccordionChange(coAppKey)}
                  paddingTopTabZero={true}
                >
                  {idx === 0 && (
                    <Grid
                      item
                      xs={12}
                      sx={{ mb: 1, p: 1 }}
                      style={{
                        backgroundColor: "#EBF9F9",
                        borderRadius: "8px",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 400,
                          fontSize: "15px",
                          color: "#5E596C",
                        }}
                      >
                        Please add the co-applicant whose financial to be
                        consider for loan evaluation
                      </Typography>
                    </Grid>
                  )}

                  {/* Tabs */}
                  <Tabs
                    value={currentTab}
                    onChange={(_e: any, newIndex: any) =>
                      handleTabChange(coAppKey, newIndex)
                    }
                    aria-label={`Tabs for ${coAppKey}`}
                    variant={isXsOrSm ? "scrollable" : "standard"}
                    {...(isXsOrSm && {
                      scrollButtons: "auto",
                      allowScrollButtonsMobile: false,
                    })}
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      "& .MuiTabs-indicator": { backgroundColor: "#00b3b5" },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#616161",
                        "&.Mui-selected": { color: "#00b3b5" },
                      },
                    }}
                  >
                    {tabLabels.map((label, i) => (
                      <Tab
                        key={label}
                        label={label}
                        disabled={!tabEnabled[coAppKey]?.[i]}
                      />
                    ))}
                  </Tabs>

                  <Box sx={{ mt: 2 }}>
                    {tabFields[currentTab].length === 0 ? (
                      <Typography
                        sx={{ mt: 2 }}
                        variant="body2"
                        color="text.secondary"
                      >
                        No fields
                      </Typography>
                    ) : (
                      <>
                        {/* ── Basic Details tab ─────────────────────────── */}
                        {tabLabels[currentTab] === "Basic Details" ? (
                          <>
                            <Grid container spacing={2}>
                              {tabFields[currentTab]
                                .filter((field: any) => {
                                  if (getOtherRelationFieldMatch(field.name)) {
                                    const relationMatch =
                                      getOtherRelationFieldMatch(field.name);
                                    return relationMatch
                                      ? isOtherRelationFieldVisible(relationMatch[1])
                                      : false;
                                  }

                                  return !(
                                    isFirstCoApplicantPayPartyField(
                                      String(field?.name || ""),
                                    ) ||
                                    /^basicCoApplicant\d+AadhaarNumber$/.test(
                                      field.name,
                                    ) ||
                                    /^basicCoApplicant\d+IsPanVerified$/.test(
                                      field.name,
                                    ) ||
                                    /^basicCoApplicant\d+IsMobileVerified$/.test(
                                      field.name,
                                    ) ||
                                    /^basicCoApplicant\d+IsEmailVerified$/.test(
                                      field.name,
                                    ) ||
                                    /^basicCoApplicant\d+AadhaarVerified$/.test(
                                      field.name,
                                    )
                                  );
                                })
                                .sort(
                                  (prev: any, next: any) =>
                                    prev?.fieldOrder - next?.fieldOrder,
                                )
                                .map((field: any) => {
                                  const panMatch = field.name.match(
                                    /^basicCoApplicant(\d+)PanNumber$/
                                  );
                                  if (panMatch) {
                                    const idx = String(
                                      Number(panMatch[1]) - 1
                                    );
                                    const coAppNum = panMatch[1];
                                    const formikPanVerified =
                                      formikRef?.current?.values?.[
                                        basicField(coAppNum, "IsPanVerified")
                                      ];
                                    const reduxPanVerified =
                                      coApplicantVerification[idx]?.panVerified;
                                    const lastPan =
                                      coApplicantVerification[idx]
                                        ?.lastPanNumber ||
                                      formikRef?.current?.values?.[field.name];
                                    const currentPanVal =
                                      formikRef?.current?.values?.[field.name];
                                    const isPanVerified =
                                      !!(formikPanVerified || reduxPanVerified) &&
                                      currentPanVal === lastPan;
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={field.name}
                                      >
                                        <Box>
                                          <label
                                            className="text-lable"
                                            style={{ display: "block" }}
                                          >
                                            {field.label}{" "}
                                            {field.required && (
                                              <span style={{ color: "red" }}>
                                                *
                                              </span>
                                            )}
                                          </label>
                                          <Field name={field.name}>
                                            {({ field: f, meta, form }: any) => (
                                              <InputField
                                                {...f}
                                                fullWidth
                                                placeholder={
                                                  field.placeholder ||
                                                  "Enter PAN Number"
                                                }
                                                variant="outlined"
                                                autoComplete="new-password"
                                                error={
                                                  !!panVerifyError[field.name] ||
                                                  (!!meta.touched && !!meta.error)
                                                }
                                                helperText={
                                                  panVerifyError[field.name]
                                                    ? panVerifyError[field.name]
                                                    : meta.touched && meta.error
                                                      ? meta.error
                                                      : ""
                                                }
                                                onChange={(e: any) => {
                                                  let val =
                                                    e.target.value?.toUpperCase();
                                                  if (val.length > 10)
                                                    val = val.slice(0, 10);
                                                  form.setFieldValue(
                                                    field.name,
                                                    val
                                                  );
                                                  setPanVerifyError((prev) => ({
                                                    ...prev,
                                                    [field.name]: "",
                                                  }));
                                                  if (val !== lastPan) {
                                                    dispatch(
                                                      setCoApplicantVerification({
                                                        index: idx,
                                                        data: {
                                                          panVerified: false,
                                                        },
                                                      })
                                                    );
                                                  }
                                                }}
                                                onInput={(e: any) => {
                                                  if (
                                                    typeof e.target.value ===
                                                      "string" &&
                                                    /^\s/.test(e.target.value)
                                                  ) {
                                                    e.target.value =
                                                      e.target.value.replace(
                                                        /^\s+/,
                                                        ""
                                                      );
                                                  }
                                                  e.target.value =
                                                    e.target.value?.toUpperCase();
                                                }}
                                                inputProps={{ maxLength: 10 }}
                                                InputProps={{
                                                  readOnly: isPanVerified,
                                                  style: {
                                                    textTransform: "uppercase",
                                                  },
                                                  endAdornment: (
                                                    <Button
                                                      sx={{
                                                        backgroundColor:
                                                          isPanVerified
                                                            ? "transparent"
                                                            : "#FEC10D",
                                                        color: isPanVerified
                                                          ? "#28C76F"
                                                          : "#000000",
                                                        "&:hover": {
                                                          backgroundColor:
                                                            isPanVerified
                                                              ? "transparent"
                                                              : "#FEC10D",
                                                          color: isPanVerified
                                                            ? "#28C76F"
                                                            : "#000000",
                                                        },
                                                        "&.Mui-disabled": {
                                                          backgroundColor:
                                                            isPanVerified
                                                              ? "transparent"
                                                              : "#FEC10D",
                                                          color: isPanVerified
                                                            ? "#28C76F"
                                                            : "#000000",
                                                          WebkitTextFillColor:
                                                            isPanVerified
                                                              ? "#28C76F"
                                                              : "#000000",
                                                          opacity: 1,
                                                        },
                                                        "& img": {
                                                          opacity: 1,
                                                          filter:
                                                            "brightness(1)",
                                                        },
                                                        textTransform: "none",
                                                        fontSize: isPanVerified
                                                          ? "16px"
                                                          : "10px",
                                                        fontWeight: isPanVerified
                                                          ? 400
                                                          : 700,
                                                        px: "15px",
                                                        borderRadius: "4px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        boxShadow: "none",
                                                        height: "100%",
                                                        mr: 0.5,
                                                      }}
                                                      disabled={
                                                        isPanVerified ||
                                                        panLoading[field.name]
                                                      }
                                                      onClick={
                                                        isPanVerified
                                                          ? undefined
                                                          : async () => {
                                                              if (isPanVerified)
                                                                return;
                                                              setPanLoading(
                                                                (prev) => ({
                                                                  ...prev,
                                                                  [field.name]: true,
                                                                })
                                                              );
                                                              try {
                                                                const panNum =
                                                                  form.values[
                                                                    field.name
                                                                  ];
                                                                const firstName =
                                                                  form.values[
                                                                    basicField(
                                                                      coAppNum,
                                                                      "FirstName"
                                                                    )
                                                                  ] || "";
                                                                const lastName =
                                                                  form.values[
                                                                    basicField(
                                                                      coAppNum,
                                                                      "LastName"
                                                                    )
                                                                  ] || "";
                                                                const name = `${firstName} ${lastName}`.trim();
                                                                const dob =
                                                                  form.values[
                                                                    basicField(
                                                                      coAppNum,
                                                                      "dob"
                                                                    )
                                                                  ] || "";
                                                                // Check one scenario at a time so only one message shows under the PAN field
                                                                const verifyError = !dob
                                                                  ? "Please enter your Date of Birth before verifying your PAN."
                                                                  : !String(panNum || "").trim()
                                                                    ? "Please enter your PAN to proceed with verification."
                                                                    : !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(String(panNum).trim().toUpperCase())
                                                                      ? "Please enter a valid 10-digit PAN number."
                                                                      : "";
                                                                setPanVerifyError((prev) => ({
                                                                  ...prev,
                                                                  [field.name]: verifyError,
                                                                }));
                                                                if (verifyError) return;
                                                                const piiPublicKey =
                                                                  await fetchSanitizedPiiPublicKey();
                                                                if (!piiPublicKey)
                                                                  return;
                                                                const payload =
                                                                  await encryptData(
                                                                    {
                                                                      panNumber:
                                                                        panNum,
                                                                      fullName:
                                                                        name,
                                                                      dob: dob
                                                                        ? new Date(
                                                                            dob
                                                                          ).toLocaleDateString(
                                                                            "en-GB"
                                                                          )
                                                                        : "",
                                                                      trackingId,
                                                                      applicantType: `Co-Applicant ${coAppNum}`,
                                                                    },
                                                                    ["panNumber"],
                                                                    piiPublicKey
                                                                  );
                                                                const resp =
                                                                  await applicantDetailsServices.verifyPan(
                                                                    payload
                                                                  );
                                                                if (
                                                                  resp.status ===
                                                                  200
                                                                ) {
                                                                  if (
                                                                    resp.data
                                                                      ?.data
                                                                      ?.dateOfBirth ===
                                                                      "invalid" &&
                                                                    dob
                                                                  ) {
                                                                    setPanVerifyError((prev) => ({
                                                                      ...prev,
                                                                      [field.name]:
                                                                        "We couldn't verify your PAN with the provided Date of Birth. Please verify your details and re-enter them.",
                                                                    }));
                                                                  } else {
                                                                    const isAadhaarSeeded =
                                                                      String(
                                                                        resp
                                                                          ?.data
                                                                          ?.data
                                                                          ?.aadhaarSeedingStatus ||
                                                                          ""
                                                                      ).toLowerCase() ===
                                                                      "y";
                                                                      
                                                                      const fieldData = getFields(fields, activeCoApplicantNum, reduxHorizontalSteps);
                                                                      callSaveCoApplicantApi(values, fieldData, String(activeCoApplicantNum), 
                                                                      trackingId, resp?.data?.data?.aadhaarSeedingStatus === "y" ? true : false,
                                                                       "PAN", {})

                                                                        trackCustomerJourneyEvent(
                                                                          `${extendedJourneyPrefix}_COAPP${activeCoApplicantNum}ADDDETAILS_COAPP${activeCoApplicantNum}BASIC_PANVERI_PAGE12`,
                                                                          decrypt(userProfile?.id) ?? "",
                                                                          trackingId,
                                                                          {
                                                                            label: `${extendedJourneyPrefix}_COAPP${activeCoApplicantNum}ADDDETAILS_COAPP${activeCoApplicantNum}BASIC_PANVERI_PAGE12`,
                                                                            category: `${extendedJourneyPrefix}_COAPP${activeCoApplicantNum}ADDDETAILS_COAPP${activeCoApplicantNum}BASIC_PANVERI_PAGE12`,
                                                                            action: `${extendedJourneyPrefix}_COAPP${activeCoApplicantNum}ADDDETAILS_COAPP${activeCoApplicantNum}BASIC_PANVERI_PAGE12`,
                                                                            page: `Extended Journey_CoApplicant Additional Details_PAN verification(${activeCoApplicantNum})`,
                                                                          }
                                                                        );
                                                                      

                                                                    dispatch(
                                                                      setCoApplicantVerification(
                                                                        {
                                                                          index:
                                                                            idx,
                                                                          data: {
                                                                            panVerified:
                                                                              true,
                                                                            lastPanNumber:
                                                                              panNum,
                                                                            aadhaarSeedingStatus:
                                                                              isAadhaarSeeded,
                                                                          },
                                                                        }
                                                                      )
                                                                    );
                                                                    formikRef?.current?.setFieldValue(
                                                                      field.name,
                                                                      panNum
                                                                    );
                                                                    formikRef?.current?.setFieldValue(
                                                                      basicField(
                                                                        coAppNum,
                                                                        "IsPanVerified"
                                                                      ),
                                                                      true,
                                                                      false
                                                                    );
                                                                    formikRef?.current?.setFieldError(
                                                                      field.name,
                                                                      undefined
                                                                    );
                                                                    dispatch(
                                                                      updateFormData(
                                                                        {
                                                                          [basicField(
                                                                            coAppNum,
                                                                            "IsPanVerified"
                                                                          )]: true,
                                                                        }
                                                                      )
                                                                    );
                                                                    setSnackbar({
                                                                      open: true,
                                                                      message:
                                                                        "Co-applicant PAN verified successfully.",
                                                                      severity:
                                                                        "success",
                                                                    });
                                                                  }
                                                                } else {
                                                                  setSnackbar({
                                                                    open: true,
                                                                    message:
                                                                      "PAN verification failed. Please check the details and try again.",
                                                                    severity:
                                                                      "error",
                                                                  });
                                                                }
                                                              } catch {
                                                                setSnackbar({
                                                                  open: true,
                                                                  message:
                                                                    "PAN verification failed. Please try again.",
                                                                  severity:
                                                                    "error",
                                                                });
                                                              } finally {
                                                                setPanLoading(
                                                                  (prev) => ({
                                                                    ...prev,
                                                                    [field.name]: false,
                                                                  })
                                                                );
                                                              }
                                                            }
                                                      }
                                                    >
                                                      {panLoading[
                                                        field.name
                                                      ] ? (
                                                        <CircularProgress
                                                          size={16}
                                                          color="inherit"
                                                        />
                                                      ) : isPanVerified ? (
                                                        <>
                                                          <img
                                                            src={greenCheckVerified}
                                                            alt="Verified"
                                                            style={{
                                                              height: "13px",
                                                              objectFit:
                                                                "contain",
                                                            }}
                                                          />
                                                          <Typography
                                                            variant="inherit"
                                                            sx={{
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            Verified
                                                          </Typography>
                                                        </>
                                                      ) : (
                                                        <>
                                                          <Typography variant="inherit">
                                                            Verify
                                                          </Typography>
                                                          <img
                                                            src={arrowRightVerify}
                                                            alt="verify"
                                                            style={{
                                                              height: "15px",
                                                              objectFit:
                                                                "contain",
                                                            }}
                                                          />
                                                        </>
                                                      )}
                                                    </Button>
                                                  ),
                                                }}
                                              />
                                            )}
                                          </Field>
                                        </Box>
                                      </Grid>
                                    );
                                  }
                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={
                                        isCoApplicantPayPartyField(
                                          String(field?.name || ""),
                                        )
                                          ? 12
                                          : 6
                                      }
                                      md={
                                        isCoApplicantPayPartyField(
                                          String(field?.name || ""),
                                        )
                                          ? 12
                                          : 4
                                      }
                                      key={field.name}
                                    >
                                      {renderField(
                                        getOtherRelationFieldMatch(field.name)
                                          ? {
                                              ...field,
                                              required: isOtherRelationFieldRequired(field),
                                            }
                                          : field,
                                      )}
                                    </Grid>
                                  );
                                })}
                            </Grid>

                            {/* Aadhaar verification UI */}
                            <Grid container className="inner-form-box" mt={2}>
                              <Grid item xs={12} sm={12} md={12}>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <img
                                    src={idIcon}
                                    alt="icon"
                                    style={{ width: 20, height: 20 }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      color: "#4B465C",
                                    }}
                                  >
                                    Verify Aadhaar Number
                                  </Typography>
                                </Box>
                                <label
                                  className="text-lable"
                                  style={{ marginBottom: "10px" }}
                                >
                                  Verify your Aadhaar to enhance processing
                                  speed and accuracy. This streamlined step is
                                  provided for your ease and remains optional,
                                  allowing you to enter details manually if you
                                  prefer.
                                </label>
                              </Grid>

                              {isAadharLoading[coAppKey] ? (
                                <Skeleton
                                  variant="rectangular"
                                  width={120}
                                  height={40}
                                  sx={{ borderRadius: 2, mb: 1 }}
                                />
                              ) : (
                                <IconButton
                                  sx={{
                                    backgroundColor:
                                      formikRef?.current?.values?.[
                                        basicField(coAppNum, "AadhaarVerified")
                                      ] === true ||
                                      isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "#33c2c4"
                                        : undefined,
                                    color:
                                      formikRef?.current?.values?.[
                                        basicField(coAppNum, "AadhaarVerified")
                                      ] === true ||
                                      isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "#33c2c4"
                                        : "#000",
                                    border:
                                      formikRef?.current?.values?.[
                                        basicField(coAppNum, "AadhaarVerified")
                                      ] === true ||
                                      isDocumentVerifiedCoApplicant[coAppKey]
                                        ? "1px solid #33c2c4"
                                        : "",
                                    "&.Mui-disabled": {
                                      backgroundColor:
                                        formikRef?.current?.values?.[
                                          basicField(
                                            coAppNum,
                                            "AadhaarVerified",
                                          )
                                        ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                          ? "white !important"
                                          : "lightgray !important",
                                      color:
                                        formikRef?.current?.values?.[
                                          basicField(
                                            coAppNum,
                                            "AadhaarVerified",
                                          )
                                        ] === true ||
                                        isDocumentVerifiedCoApplicant[coAppKey]
                                          ? "#33c2c4"
                                          : "#000",
                                    },
                                  }}
                                  onClick={async () => {
                                    const values =
                                      formikRef?.current?.values || {};
                                    const email =
                                      values[basicField(coAppNum, "Email")] ||
                                      "";
                                    const firstName =
                                      values[
                                        basicField(coAppNum, "FirstName")
                                      ] || "";
                                    const lastName =
                                      values[
                                        basicField(coAppNum, "LastName")
                                      ] || "";
                                    const phoneNumber =
                                      values[
                                        basicField(coAppNum, "PhoneNumber")
                                      ] || "";

                                    const isPanVerified =
                                      values[
                                        basicField(coAppNum, "IsPanVerified")
                                      ] === true;
                                    const isMobileValid =
                                      String(phoneNumber)
                                        .replace(/\D/g, "")
                                        .length === 10;
                                    const isEmailValid =
                                      JOURNEY_EMAIL_REGEX.test(
                                        String(email),
                                      );

                                    if (!isPanVerified || !isMobileValid || !isEmailValid) {
                                      setSnackbar({
                                        open: true,
                                        message:
                                          "To verify Aadhaar, please complete PAN verification and enter valid mobile and email.",
                                        severity: "error",
                                      });
                                      return;
                                    }

                                    setIsAadharLoading((prev) => ({
                                      ...prev,
                                      [coAppKey]: true,
                                    }));

                                    try {
                                      const panNumber =
                                        values[
                                          basicField(coAppNum, "PanNumber")
                                        ] || "";
                                      const aadhaarPayload: any = {
                                        applicantEmail: email?.toLowerCase(),
                                        applicantName:
                                          `${firstName} ${lastName}`.trim(),
                                        applicantMobile: phoneNumber,
                                        applicantPan: panNumber,
                                        redirectUrl: `${window.location.origin}/application/apply-now`,
                                        landingPageNotRequired: true,
                                        trackingId,
                                        typeOfApplicant: `Co-Applicant ${coAppNum}`,
                                      };

                                      const resp = await postsServices.addPost(
                                        "api/v1/applicant/create-app-via-aadhaar-phone",
                                        aadhaarPayload,
                                      );

                                      if (
                                        resp.data?.status === "success" &&
                                        resp.data.data?.link
                                      ) {
                                        // trackCustomerJourneyEvent(
                                        //   `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                        //   isAuthenticated
                                        //     ? (decrypt(userProfile?.id) ?? "")
                                        //     : "",
                                        //   trackingId,
                                        //   {
                                        //     label: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                        //     category: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                        //     action: `${handleGAProvisionalOfferStatments}_LOANDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE10`,
                                        //     page: `ProvisionalOff_Loan Details_COAPP(${coAppNum}) Aadhaar Verification`,
                                        //   },
                                        // );

                                          trackCustomerJourneyEvent(
                                            `${extendedJourneyPrefix}_COAPP${coAppNum}ADDDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE12`,
                                            decrypt(userProfile?.id) ?? "",
                                            trackingId,
                                            {
                                              label: `${extendedJourneyPrefix}_COAPP${coAppNum}ADDDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE12`,
                                              category: `${extendedJourneyPrefix}_COAPP${coAppNum}ADDDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE12`,
                                              action: `${extendedJourneyPrefix}_COAPP${coAppNum}ADDDETAILS_COAPP${coAppNum}BASIC_AADHAARVERI_PAGE12`,
                                              page: `Extended Journey_CoApplicant Additional Details_aadhaar verification(${coAppNum})`,
                                            }
                                          );


                                        let link = resp.data.data.link;
                                        const applicationNo =
                                          resp.data.data.applicationNo;
                                        localStorage.setItem(
                                          "applicationNo",
                                          applicationNo,
                                        );
                                        localStorage.setItem(
                                          "aadhaarCoAppKey",
                                          coAppKey,
                                        );
                                        dispatch(
                                          setAadhaarApplicationNoCoApplicant({
                                            coAppKey,
                                            applicationNo,
                                          }),
                                        );
                                        if (!/^https?:\/\//i.test(link)) {
                                          link = "https://" + link;
                                        }

                                        // Persist all current form values to Redux so they survive the page reload
                                        // after Aadhaar verification journey.
                                        dispatch(updateFormData(values));
                                        // Force Redux persist to flush to sessionStorage before navigating away
                                        await persistor.flush();

                                        window.location.replace(link);
                                      } else {
                                        setSnackbar({
                                          open: true,
                                          message: resp.data?.message,
                                          severity: "success",
                                        });
                                      }
                                    } catch (err: any) {
                                      setSnackbar({
                                        open: true,
                                        message: err?.response?.data?.message,
                                        severity: "error",
                                      });
                                    } finally {
                                      setIsAadharLoading((prev) => ({
                                        ...prev,
                                        [coAppKey]: false,
                                      }));
                                    }
                                  }}
                                  disabled={
                                    formikRef?.current?.values?.[
                                      basicField(coAppNum, "AadhaarVerified")
                                    ] === true ||
                                    isDocumentVerifiedCoApplicant[coAppKey]
                                  }
                                >
                                  {formikRef?.current?.values?.[
                                    basicField(coAppNum, "AadhaarVerified")
                                  ] === true ||
                                  isDocumentVerifiedCoApplicant[coAppKey] ? (
                                    <img src={VerifiedIcon} alt="verified" />
                                  ) : (
                                    <></>
                                  )}
                                  {formikRef?.current?.values?.[
                                    basicField(coAppNum, "AadhaarVerified")
                                  ] === true ||
                                  isDocumentVerifiedCoApplicant[coAppKey]
                                    ? "Verified"
                                    : "Verify Via Digilocker"}
                                  {!(
                                    formikRef?.current?.values?.[
                                      basicField(coAppNum, "AadhaarVerified")
                                    ] === true ||
                                    isDocumentVerifiedCoApplicant[coAppKey]
                                  ) && (
                                    <img
                                      src={RightBlackArrow}
                                      alt="right-arrow"
                                    />
                                  )}
                                </IconButton>
                              )}
                            </Grid>
                          </>
                        ) : (
                          /* ── Address / Employment tabs ─────────────────── */
                          <Grid container spacing={2}>
                            {tabLabels[currentTab] === "Address Details"
                              ? (() => {
                                  const addressFields = tabFields[currentTab];
                                  let permanentFields = addressFields.filter(
                                    (f: any) => PERM_REGEX.test(f.name) || f.name?.includes("SameAs"),
                                  );
                                  const currentFields = addressFields.filter(
                                    (f: any) => CURR_REGEX.test(f.name),
                                  );
                                  const sameAsField =
                                    addressFields.find((f: any) =>
                                      SAME_AS_PERM_REGEX.test(f.name),
                                    ) || null;

                                  const sameAsOptions = [
                                  "Applicant",
                                  ...coApplicantKeys
                                    .map((key) =>
                                      Number(key.replace("CoApplicant", ""))
                                    )
                                    .filter(
                                      (coAppNumber) =>
                                        coAppNumber < Number(activeCoApplicantNum)
                                    )
                                    .sort((a, b) => a - b)
                                    .map(
                                      (coAppNumber) =>
                                        `Co-Applicant ${coAppNumber}`
                                    ),
                                ];

                                let tempFields = JSON.parse(JSON.stringify(permanentFields));
                                tempFields?.forEach((item: any) => {
                                  if (
                                    item.name ===
                                    `addressCoApplicant${activeCoApplicantNum}SameAs`
                                  ) {
                                    item.options.options = sameAsOptions;
                                  }
                                });
                                permanentFields = tempFields;
                                  return (
                                    <>
                                    <Grid container xs={12}>
                                      <Grid item xs={12} sm={4} sx={{ paddingLeft: "16px" }}>
                                        {permanentFields[0]?.name === `addressCoApplicant${activeCoApplicantNum}SameAs` && renderField(permanentFields[0])}
                                      </Grid>
                                      <Grid item xs={12} sm={4}></Grid><Grid item xs={12} sm={4}>
                                      </Grid>
                                    </Grid>
                                      {permanentFields.length > 0 && (
                                        <Grid item xs={12}>
                                          <SectionHeader
                                            icon={
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  width: 40,
                                                  height: 40,
                                                  borderRadius: "8px",
                                                  bgcolor: "#e5feffff",
                                                }}
                                              >
                                                <img
                                                  src={locationIcon}
                                                  alt="icon"
                                                  style={{
                                                    width: 28,
                                                    height: 28,
                                                  }}
                                                />
                                              </Box>
                                            }
                                            title="Permanent Address"
                                            subtitle=""
                                          />
                                        </Grid>
                                      )}
                                      {permanentFields
                                        .sort(
                                          (prev: any, next: any) =>
                                            prev?.fieldOrder - next?.fieldOrder,
                                        )
                                        .map((field: any) => { 
                                          if (field.name === `addressCoApplicant${activeCoApplicantNum}SameAs`) {
                                            return null;
                                          }
                                          return (
                                          <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={field.name}
                                          >
                                            {renderField(field)}
                                          </Grid>
                                        )})}

                                      {sameAsField && (
                                        <Grid
                                          item
                                          xs={12}
                                          key={sameAsField.name}
                                        >
                                          {renderField(sameAsField)}
                                        </Grid>
                                      )}
                                      {currentFields.length > 0 && (
                                        <Grid item xs={12}>
                                          <SectionHeader
                                            icon={
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  width: 40,
                                                  height: 40,
                                                  borderRadius: "8px",
                                                  bgcolor: "#e5feffff",
                                                }}
                                              >
                                                <img
                                                  src={locationIcon}
                                                  alt="icon"
                                                  style={{
                                                    width: 28,
                                                    height: 28,
                                                  }}
                                                />
                                              </Box>
                                            }
                                            title="Current Address"
                                            subtitle=""
                                          />
                                        </Grid>
                                      )}
                                      {currentFields
                                        .sort(
                                          (prev: any, next: any) => {
                                            const prevIsOwnership = /OwnershipStatus$/i.test(
                                              prev?.name || "",
                                            );
                                            const nextIsOwnership = /OwnershipStatus$/i.test(
                                              next?.name || "",
                                            );
                                            if (prevIsOwnership && !nextIsOwnership)
                                              return -1;
                                            if (!prevIsOwnership && nextIsOwnership)
                                              return 1;
                                            return (
                                              (prev?.fieldOrder ?? 0) -
                                              (next?.fieldOrder ?? 0)
                                            );
                                          },
                                        )
                                        .map((field: any) => (
                                          <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            key={field.name}
                                          >
                                            {renderField(field)}
                                          </Grid>
                                        ))}
                                    </>
                                  );
                                })()
                              : tabLabels[currentTab] ===
                                  "Employment Details" &&
                                (() => {
                                  const employmentFields = tabFields[
                                    currentTab
                                  ].filter((f: any) => EMP_REGEX.test(f.name));

                                  const isEmployedField = employmentFields.find(
                                    (f: any) =>
                                      f.type === "radio" &&
                                      /^employmentCoApplicant\d+(isEmployed|isEmployed)$/.test(
                                        f.name,
                                      ),
                                  );
                                  const isFirstCoApplicantEmployed =
                                    isFirstCoApplicantEmploymentField(
                                      isEmployedField?.name,
                                    );
                                  if (isFirstCoApplicantEmployed) {
                                    ensureFirstCoApplicantEmploymentYes(
                                      formikRef,
                                      isEmployedField?.name,
                                    );
                                  }
                                  const formikIsEmployedValue = isEmployedField
                                    ? formikRef?.current?.values?.[
                                        isEmployedField.name
                                      ]
                                    : "";
                                  const isEmployedValue = isFirstCoApplicantEmployed
                                    ? "Yes"
                                    : formikIsEmployedValue ?? "";

                                  return (
                                    <>
                                      {/* Field 1: isEmployed radio — full width */}
                                      {isEmployedField && (
                                        <Grid item xs={12} key={isEmployedField.name}>
                                            {isFirstCoApplicantEmployed && (
                                              <FirstCoApplicantEmploymentNotice />
                                            )}
                                          {renderField(
                                              lockFirstCoApplicantEmploymentField(
                                                isEmployedField,
                                              ),
                                            )}
                                        </Grid>
                                      )}

                                      {isEmployedValue === "Yes" && (
                                        <>
                                          {(() => {
                                            let hasInsertedCompanyHeader = false;
                                            const allEmploymentFields =
                                              employmentFields.filter(
                                                (f: any) =>
                                                  f.name !==
                                                  isEmployedField?.name,
                                              );
                                            const employmentTypeValue =
                                              formikRef?.current?.values?.[
                                                empField(
                                                  coAppNum,
                                                  "employmentType",
                                                )
                                              ];
                                            const selectedEmploymentType =
                                              getAutocompleteOptionValue(
                                                employmentTypeValue,
                                              );
                                            const shouldShowOnlyRequired =
                                              selectedEmploymentType === "Others" ||
                                              selectedEmploymentType === "Farmer";
                                            const fieldsToRender =
                                              !selectedEmploymentType
                                                ? allEmploymentFields.filter(
                                                    (f: any) =>
                                                      /^employmentCoApplicant\d+employmentType$/.test(
                                                        f.name,
                                                      ),
                                                  )
                                                : shouldShowOnlyRequired
                                                ? allEmploymentFields.filter(
                                                    (f: any) =>
                                                      /^employmentCoApplicant\d+employmentType$/.test(
                                                        f.name,
                                                      ) || 
                                                      /^employmentCoApplicant\d+monthlyObligation$/.test(
                                                        f.name,
                                                      ) || 
                                                      /^employmentCoApplicant\d+monthlyIncome$/.test(
                                                        f.name,
                                                      ),
                                                  )
                                                : allEmploymentFields;

                                            return fieldsToRender.map(
                                              (field: any, fieldIdx: number) => {
                                                const elements: React.ReactNode[] = [];
                                                const isEmploymentTypeField =
                                                  /^employmentCoApplicant\d+employmentType$/.test(
                                                    field.name || "",
                                                  );

                                                if (
                                                  !hasInsertedCompanyHeader &&
                                                  field.name
                                                    ?.toLowerCase()
                                                    .includes("company") &&
                                                  !/^employmentCoApplicant\d+companyName$/.test(
                                                    field.name,
                                                  )
                                                ) {
                                                  elements.push(
                                                    <Grid
                                                      item
                                                      xs={12}
                                                      key="coapp-company-section-header"
                                                    >
                                                      <SectionHeader
                                                        icon={
                                                          <Box
                                                            sx={{
                                                              display: "flex",
                                                              alignItems: "center",
                                                              justifyContent: "center",
                                                              width: 40,
                                                              height: 40,
                                                              borderRadius: "8px",
                                                              bgcolor: "#e5feffff",
                                                            }}
                                                          >
                                                            <img
                                                              src={locationIcon}
                                                              alt="icon"
                                                              style={{
                                                                width: 28,
                                                                height: 28,
                                                              }}
                                                            />
                                                          </Box>
                                                        }
                                                        title="Company Address"
                                                        subtitle=""
                                                      />
                                                    </Grid>,
                                                  );
                                                  hasInsertedCompanyHeader = true;
                                                }

                                                // First two employment detail fields are full width; rest are 6 col
                                                elements.push(
                                                  <Grid
                                                    item
                                                    xs={12}
                                                    sm={fieldIdx < 1 ? 12 : field.name.match(/^employmentCoApplicant\d+employmentYear$/)?12:6}
                                                    key={field.name}
                                                  >
                                                    <Box
                                                      sx={
                                                        isEmploymentTypeField
                                                          ? {
                                                              width: {
                                                                xs: "100%",
                                                                sm: "50%",
                                                              },
                                                            }
                                                          : undefined
                                                      }
                                                    >
                                                      {renderField(
                                                        withOptionalCompanyName(
                                                          withMandatoryCompanyAddress(
                                                            withMandatoryEmploymentAmount(field),
                                                            formValues,
                                                          ),
                                                          formValues,
                                                        ),
                                                      )}
                                                    </Box>
                                                  </Grid>,
                                                );

                                                return elements;
                                              },
                                            );
                                          })()}
                                        </>
                                      )}
                                    </>
                                  );
                                })()}
                          </Grid>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Save & Next / Save button */}
                  <Grid item xs={12} sx={{ mt: 2, textAlign: "left" }}>
                    <PrimaryButton
                      sx={{ px: 6, width: { xs: "100%", sm: "auto" } }}
                      // Only the mechanical guards disable Save & Next now. The
                      // completeness checks below moved into onClick via
                      // getTabBlockReason, so an incomplete tab reports WHY
                      // instead of leaving the button dead and unexplained.
                      disabled={zipCodeLoading || isSaveNextLoading}
                      onClick={() => {
                        const getTabBlockReason = (): string | null => {
                        const isEmployedFieldName =
                          tabFields[currentTab].find(
                            (f: any) =>
                              /^employmentCoApplicant\d+(isEmployed|isEmployed)$/.test(
                                f.name,
                              ),
                          )?.name || empField(coAppNum, "isEmployed");
                        const employmentTypeFieldName = empField(
                          coAppNum,
                          "employmentType",
                        );
                        const isEmployed = formValues?.[isEmployedFieldName];

                        const requiredFields = tabFields[currentTab].filter(
                          (rawField: any) => {
                            const f = withOptionalCompanyName(
                              withMandatoryCompanyAddress(
                                withMandatoryEmploymentAmount(rawField),
                                formValues,
                              ),
                              formValues,
                            );
                            if (
                              isFirstCoApplicantPayPartyField(
                                String(f?.name || ""),
                              )
                            ) {
                              return false;
                            }

                            const isVerificationField =
                              /^basicCoApplicant\d+(AadhaarVerified|IsMobileVerified|IsEmailVerified)$/.test(
                                f.name,
                              );
                            if (isVerificationField) return false;

                            // In Employment tab, if "No" is selected, only keep the isEmployed radio required.
                            if (
                              tabLabels[currentTab] === "Employment Details" &&
                              isEmployed === "No"
                            ) {
                              return f.name === isEmployedFieldName;
                            }

                            if (
                              f.name === employmentTypeFieldName &&
                              isEmployed === "No"
                            ) {
                              return false;
                            }
                            if (
                              tabLabels[currentTab] === "Employment Details" &&
                              isEmployed === "Yes" &&
                              !getAutocompleteOptionValue(
                                formValues?.[employmentTypeFieldName],
                              ) &&
                              f.name !== employmentTypeFieldName
                            ) {
                              return false;
                            }
                            const selectedEmploymentType =
                              getAutocompleteOptionValue(
                                formValues?.[employmentTypeFieldName],
                              );
                            if( tabLabels[currentTab] === "Employment Details" && isEmployed === "Yes" && (selectedEmploymentType === "Others" || selectedEmploymentType === "Farmer") && f.name !== employmentTypeFieldName && f.name !== empField(coAppNum, "monthlyObligation") && f.name !== empField(coAppNum, "monthlyIncome")){
                              return false;
                            }
                            return isOtherRelationFieldRequired(f);
                          },
                        );
                        const hasEmptyRequired = requiredFields.some(
                          (f: any) => {
                            const val = formValues?.[f.name];
                            return (
                              val === undefined || val === null || val === ""
                            );
                          },
                        );

                        const isPanVerified =
                          formValues?.[
                            basicField(coAppNum, "IsPanVerified")
                          ] ?? false;

                        const mobileValue: string =
                          formValues?.[
                            basicField(coAppNum, "PhoneNumber")
                          ] || "";
                        const isMobileIncomplete =
                          tabLabels[currentTab] === "Basic Details" &&
                          mobileValue && mobileValue?.toString()?.replace(/\D/g, "").length !== 10;

                        const emailValue: string =
                          formValues?.[
                            basicField(coAppNum, "Email")
                          ] ?? "";
                        const isEmailInvalid =
                          tabLabels[currentTab] === "Basic Details" &&
                          !JOURNEY_EMAIL_REGEX.test(emailValue);

                          const monthlyIncome =
                          formValues?.[
                            empField(coAppNum, "monthlyIncome")
                          ] ?? false;

                          const monthlyObligation =
                          formValues?.[
                            empField(coAppNum, "monthlyObligation")
                          ] ?? false;

                          if(monthlyIncome) {
                            delete errors[empField(coAppNum, "monthlyIncome")];
                          }

                          if(monthlyObligation) {
                            delete errors[empField(coAppNum, "monthlyObligation")];
                          }


                        // Fields like the address "years in current
                        // address/city" ones are filled but still fail Yup's
                        // max-150 check — hasEmptyRequired only catches blank
                        // values, so check formik's own error state too.
                        // Dropdown/autocomplete fields are excluded here: their
                        // Yup schema only ever checks non-emptiness (no format/
                        // length rules), which hasEmptyRequired already covers
                        // from `values` directly. Their onChange handlers make
                        // multiple setFieldValue calls per selection (e.g. to
                        // also set a companion "...Name" field), and Formik
                        // validates each call against a stale pre-update
                        // snapshot — so `errors` for these fields can be left
                        // with a stale, invisible (untouched) entry that would
                        // otherwise block Save & Next until an unrelated field
                        // is touched and forces revalidation.
                        const hasTabErrors = tabFields[currentTab].some(
                          (f: any) =>
                            f?.type !== "dropdown" &&
                            f?.type !== "autocomplete" &&
                            Boolean(errors?.[f.name]),
                        );

                          // Same conditions as the old `disabled` expression,
                          // each now carrying the reason it blocked.
                          if (isPanVerified === false) {
                            return "Please verify the co-applicant's PAN before proceeding.";
                          }
                          if (isMobileIncomplete) {
                            return "Please enter a valid 10-digit mobile number.";
                          }
                          if (isEmailInvalid) {
                            return "Please enter a valid email address (e.g., name@example.com).";
                          }
                          if (hasEmptyRequired || hasTabErrors) {
                            return "Please fill in all the required details correctly before proceeding.";
                          }
                          return null;
                        };

                        const tabBlockReason = getTabBlockReason();
                        if (tabBlockReason) {
                          // Mark this tab's fields touched so their own inline
                          // messages appear alongside the summary, without
                          // touching the tabs the user hasn't saved yet.
                          tabFields[currentTab].forEach((f: any) => {
                            const fieldName = String(f?.name || "");
                            if (fieldName && errors?.[fieldName]) {
                              formikRef?.current?.setFieldTouched(
                                fieldName,
                                true,
                                false,
                              );
                            }
                          });
                          // Error snackbar is shown only on the main Continue button
                          return;
                        }

                        handleTabSave(
                          coAppKey,
                          currentTab,
                          currentTab === tabLabels.length - 1,
                        );
                        dispatch(
                          setVerificationMessage({
                            index: "",
                            applicantType: "",
                            field: "",
                            status: "",
                            message: "",
                          }),
                        );
                      }}
                    >
                      {isSaveNextLoading ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress
                            size={16}
                            sx={{ color: "#1F2233" }}
                          />
                          <span>Loading...</span>
                        </Box>
                      ) : currentTab === tabLabels.length - 1 ? (
                        "Save"
                      ) : (
                        "Save & Next"
                      )}
                    </PrimaryButton>
                  </Grid>
                </CollapsibleSection>

                {/* Remove button (only for dynamically added co-applicants) */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContinueent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {group.basic
                    .concat(group.address, group.employment)
                    .some((field: any) => field.isRemoved) ? (
                    <Grid sx={{ textAlign: "left", mt: 2 }}>
                      <Box
                        sx={{ display: "inline-flex", cursor: "pointer" }}
                        onClick={() => handleRemoveClick(group.ids)}
                      >
                        <img src={RemoveIcon} alt="remove" />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            color: "#FF4C51",
                            fontSize: "13px",
                          }}
                        >
                          Remove
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                    <Box />
                  )}
                </Grid>
              </Grid>
            </React.Fragment>
          );
        });
      })()}

      <LoaderBackdrop open={Object.values(isAadharLoading).some(Boolean)} />

      {/* Confirmation modal */}
      <ModalPopup
        isOpen={confirmOpen}
        onClose={handleCancelRemove}
        subTitle="Remove Co-Applicant"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this Co-Applicant? This action
            cannot be undone.
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
          >
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton onClick={handleConfirmRemove} variant="contained">
              Confirm
            </PrimaryButton>
          </Box>
        </Box>
      </ModalPopup>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CoApplicantAdditionalDetails;